// Calendar week view with subtasks on correct day columns
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, useDraggable, useDroppable } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { getJobs, type Job } from '@/lib/api/jobs';
import { getClients, type Client } from '@/lib/api/clients';
import { getCrews } from '@/lib/api/crews';
import { getScheduleEvents, updateScheduleEvent, updateChecklistItem, type ScheduleEvent } from '@/lib/api/schedule';
import { JobSchedulingForm } from '@/components/jobs/JobSchedulingForm';
import { WorkerAssignPanel } from '@/components/scheduling/WorkerAssignPanel';
import { CalendarSubscribe } from '@/components/scheduling/CalendarSubscribe';
import { getJobById, updateScheduledJob, type JobScheduleData } from '@/lib/api/jobs';
import { isHoliday, type Holiday } from '@/lib/utils/holidays';
import { bcStatHolidays } from '@/lib/utils/bcHolidays';
import { getForecast, weatherIcon, type DayForecast } from '@/lib/api/weather';
import { getAgenda, agendaByDay, AGENDA_META, AGENDA_EXTRA_TYPES, type AgendaItem, type AgendaType } from '@/lib/api/agenda';
import { jobPath } from '@/lib/utils/navPaths';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { useAuth } from '@/lib/hooks/useAuth';
import { getAllTimeEntries } from '@/lib/api/portal';
import { getEmployees, type Employee } from '@/lib/api/employees';
import { DayLogModal, type DayEntry, type SaveResult } from '@/components/schedule/DayLogModal';

// Local YYYY-MM-DD (avoids the UTC off-by-one from toISOString()).
const ymdLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;


// Draggable Event Component with Resize
const DraggableEvent = ({ event, children, onResize, onDoubleClick, isDragLocked }: { 
  event: ScheduleEvent; 
  children: React.ReactNode;
  onResize?: (eventId: number | string, newEndTime: string) => void;
  onDoubleClick?: (event: ScheduleEvent) => void;
  isDragLocked?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: event.id.toString(),
    data: { event },
    disabled: isDragLocked,
  });

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    const startY = e.clientY;
    const startTime = event.end_time || event.endTime || '';
    const startHour = parseInt(startTime.split(':')[0]);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const hoursDelta = Math.round(deltaY / 96); // 96px per hour (h-24 = 6rem = 96px)
      const newEndHour = Math.max(startHour + 1, Math.min(15.5, startHour + hoursDelta));
      const minutes = (newEndHour % 1) * 60;
      const newEndTime = `${String(Math.floor(newEndHour)).padStart(2, '0')}:${String(Math.round(minutes)).padStart(2, '0')}:00`;
      
      if (onResize && newEndHour !== startHour) {
        onResize(event.id, newEndTime);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      ref={setNodeRef}
      {...(isDragLocked ? {} : listeners)}
      {...attributes}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`relative group ${isDragLocked ? 'cursor-default' : 'cursor-move'}`}
      onDoubleClick={() => onDoubleClick?.(event)}
    >
      {children}
      {onResize && !isDragLocked && (
        <div 
          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 hover:bg-primary/30 transition-opacity"
          onMouseDown={handleResizeMouseDown}
          title="Drag to resize"
        />
      )}
    </div>
  );
};

// Droppable Cell Component
const DroppableCell = ({ id, date, hour, children, className, onDoubleClick, style }: { 
  id: string; 
  date: string; 
  hour?: number; 
  children?: React.ReactNode;
  className?: string;
  onDoubleClick?: () => void;
  style?: React.CSSProperties;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { date, hour },
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'bg-primary/10 ring-2 ring-primary' : ''}`}
      onDoubleClick={onDoubleClick}
      style={style}
    >
      {children}
    </div>
  );
};

type ViewMode = 'Day' | 'Week' | 'Month' | 'Year' | 'Gantt';

interface Task {
  id: string;
  name: string;
  category: string;
  duration: string;
}

interface JobTimelineProps {
  preselectedJobId?: string;
  preselectedClientId?: string;
  initialViewMode?: ViewMode;
  initialDate?: Date;
  onTaskBankPropsChange?: (props: any) => void;
}

export const JobTimeline = ({ 
  preselectedJobId, 
  preselectedClientId,
  initialViewMode,
  initialDate,
  onTaskBankPropsChange 
}: JobTimelineProps) => {
  const navigate = useNavigate();
  const { toasts, removeToast, toast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>(
    initialViewMode || ((localStorage.getItem('calendarViewMode') as ViewMode) || 'Week')
  );
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [crews, setCrews] = useState<any[]>([]);
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string>(preselectedJobId || '');
  const [selectedClientId, setSelectedClientId] = useState<string>(preselectedClientId || '');
  const [startDate] = useState<string>('');
  const [endDate] = useState<string>('');
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [loading, setLoading] = useState(true);
  const [activeEvent, setActiveEvent] = useState<ScheduleEvent | null>(null);
  const [isAlertsExpanded, setIsAlertsExpanded] = useState(true);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isDragLocked, setIsDragLocked] = useState(
    () => localStorage.getItem('calendarDragLocked') !== 'false'
  );
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null);
  const [editingJobDetails, setEditingJobDetails] = useState<any>(null);

  // ── Timesheet hours-logging overlay (integrated into the calendar) ──────
  const { user } = useAuth();
  const [hoursMode, setHoursMode] = useState(() => localStorage.getItem('calendarHoursMode') === 'true');
  const [timeEntries, setTimeEntries] = useState<DayEntry[]>([]);
  const [hoursEmployees, setHoursEmployees] = useState<Employee[]>([]);
  const [hoursJobs, setHoursJobs] = useState<Job[]>([]);
  const [hoursLoaded, setHoursLoaded] = useState(false);
  const [hoursModalDate, setHoursModalDate] = useState<string | null>(null);

  const loadHoursData = useCallback(async () => {
    try {
      const [ents, emps] = await Promise.all([getAllTimeEntries(), getEmployees()]);
      setTimeEntries(ents);
      setHoursEmployees(emps.filter(e => e.status === 'active'));
      getJobs({ limit: 200 }).then(r => setHoursJobs(r.jobs)).catch(() => {});
      setHoursLoaded(true);
    } catch {
      toast.error('Failed to load time entries');
    }
  }, [toast]);

  // Lazy-load hours data the first time the overlay is switched on.
  useEffect(() => {
    localStorage.setItem('calendarHoursMode', String(hoursMode));
    if (hoursMode && !hoursLoaded) loadHoursData();
  }, [hoursMode, hoursLoaded, loadHoursData]);

  const entriesByDate = useMemo(() => {
    const m = new Map<string, DayEntry[]>();
    for (const e of timeEntries) {
      const key = String(e.work_date).slice(0, 10);
      const arr = m.get(key) || [];
      arr.push(e);
      m.set(key, arr);
    }
    return m;
  }, [timeEntries]);

  const myEmployeeId = useMemo(() => {
    const email = user?.email?.toLowerCase();
    return hoursEmployees.find(e => e.email?.toLowerCase() === email)?.id ?? null;
  }, [hoursEmployees, user]);

  const sortedHoursEmployees = useMemo(() => {
    return [...hoursEmployees].sort((a, b) => {
      if (a.id === myEmployeeId) return -1;
      if (b.id === myEmployeeId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [hoursEmployees, myEmployeeId]);

  // Past-or-today weekdays (Mon–Fri) in the visible month with zero entries.
  const missingHoursDays = useMemo(() => {
    if (!hoursMode) return [] as string[];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const todayStr = ymdLocal(new Date());
    const out: string[] = [];
    const d = new Date(year, month, 1);
    while (d.getMonth() === month) {
      const key = ymdLocal(d);
      const isWeekday = d.getDay() >= 1 && d.getDay() <= 5;
      if (isWeekday && key <= todayStr && !entriesByDate.has(key)) out.push(key);
      d.setDate(d.getDate() + 1);
    }
    return out;
  }, [hoursMode, currentDate, entriesByDate]);

  const hoursDateLabel = (key: string) =>
    new Date(key + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  const handleHoursSaved = (result: SaveResult) => {
    setHoursModalDate(null);
    loadHoursData();
    if (result.noWork) toast.success('Day marked as no work');
    if (result.ok > 0) toast.success(`Logged ${result.ok} time entr${result.ok === 1 ? 'y' : 'ies'} — auto-approved`);
    if (result.failed.length > 0) toast.error(`Failed for: ${result.failed.join(', ')}`);
  };

  // Job scheduled on a given day → preselect it in the log-hours dialog.
  const jobIdForDate = (dateStr: string): number | null => {
    for (const ev of scheduleEvents) {
      const e = ev as unknown as Record<string, unknown>;
      const s = String(e.scheduled_date || e.scheduledDate || '').slice(0, 10);
      if (!s) continue;
      const end = String(e.end_date || e.endDate || s).slice(0, 10);
      if (dateStr >= s && dateStr <= end) {
        const jid = e.job_id ?? e.jobId;
        if (jid != null) return Number(jid);
      }
    }
    return null;
  };

  // Update URL when calendar state changes
  const updateURL = (date: Date, mode: ViewMode, jobId?: string, clientId?: string) => {
    const dateStr = `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${String(date.getFullYear()).slice(-2)}`;
    const viewType = mode.toLowerCase();
    
    const params = new URLSearchParams();
    if (jobId) params.set('jobId', jobId);
    if (clientId) params.set('clientId', clientId);
    
    const queryString = params.toString();
    const url = `/calendar/${viewType}/${dateStr}${queryString ? `?${queryString}` : ''}`;
    
    navigate(url, { replace: true });
  };

  // Track if this is the initial mount
  const [isInitialMount, setIsInitialMount] = React.useState(true);

  // Update URL when view mode, date, job, or client changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    updateURL(currentDate, viewMode, selectedJobId, selectedClientId);
  }, [viewMode, currentDate, selectedJobId, selectedClientId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  // Update task bank props when data changes
  useEffect(() => {
    if (onTaskBankPropsChange) {
      const tasks: Task[] = [];
      onTaskBankPropsChange({
        tasks,
        isAlertsExpanded,
        setIsAlertsExpanded,
        getPastDueTasks,
        getConflictingTasks,
        crews,
        getConflictReasons,
      });
    }
  }, [isAlertsExpanded, crews, scheduleEvents]);

  // Auto-select client when preselected job is loaded
  useEffect(() => {
    if (preselectedJobId && allJobs.length > 0 && !selectedClientId) {
      const preselectedJob = allJobs.find(job => job.id.toString() === preselectedJobId);
      if (preselectedJob && preselectedJob.clientId) {
        setSelectedClientId(preselectedJob.clientId.toString());
      }
    }
  }, [preselectedJobId, allJobs, selectedClientId]);

  // Filter jobs when client changes
  useEffect(() => {
    if (selectedClientId) {
      const filteredJobs = allJobs.filter(job => job.clientId?.toString() === selectedClientId);
      setJobs(filteredJobs);
      // Clear job selection if it's not in the filtered list
      if (selectedJobId && !filteredJobs.find(j => j.id.toString() === selectedJobId)) {
        setSelectedJobId('');
      }
    } else {
      setJobs(allJobs);
    }
  }, [selectedClientId, allJobs]);

  // Load schedule events when filters change OR when jobs load
  useEffect(() => {
    if (allJobs.length > 0) {
      loadScheduleEvents();
    }
  }, [selectedJobId, selectedClientId, startDate, endDate, viewMode, currentDate, allJobs.length]);

  // Load holidays when year changes — BC statutory holidays only (CR #200)
  useEffect(() => {
    const year = currentDate.getFullYear();
    const toHolidays = (y: number): Holiday[] =>
      bcStatHolidays(y).map(h => ({
        date: new Date(h.date + 'T12:00:00'),
        name: h.name,
        type: 'provincial' as const,
        provinces: ['BC'],
      }));
    // Include previous and next year for edge cases at year boundaries
    setHolidays([...toHolidays(year - 1), ...toHolidays(year), ...toHolidays(year + 1)]);
  }, [currentDate]);

  // Region forecast (Duncan/Cowichan, 14 days) keyed by local date (CR #201)
  const [forecastByDate, setForecastByDate] = useState<Map<string, DayForecast>>(new Map());
  useEffect(() => {
    getForecast()
      .then(days => setForecastByDate(new Map(days.map(d => [d.date, d]))))
      .catch(() => {});
  }, []);

  // ── Agenda overlay (CR #206): every dated record in the system ────────────
  // One fetch per visible month span (whole months covering the visible range,
  // so day/week navigation inside a month never refetches).
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const agendaRangeRef = React.useRef('');
  useEffect(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    let rangeStart: Date;
    let rangeEnd: Date;
    if (viewMode === 'Year') {
      rangeStart = new Date(y, 0, 1);
      rangeEnd = new Date(y, 11, 31);
    } else if (viewMode === 'Week') {
      rangeStart = new Date(currentDate);
      rangeStart.setDate(rangeStart.getDate() - rangeStart.getDay());
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 6);
    } else {
      rangeStart = new Date(y, m, currentDate.getDate());
      rangeEnd = rangeStart;
    }
    // Snap to whole months
    const start = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    const end = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth() + 1, 0);
    const key = `${ymdLocal(start)}|${ymdLocal(end)}`;
    if (agendaRangeRef.current === key) return;
    agendaRangeRef.current = key;
    getAgenda(ymdLocal(start), ymdLocal(end))
      .then(setAgendaItems)
      .catch(() => setAgendaItems([]));
  }, [viewMode, currentDate]);

  const agendaByDate = useMemo(() => agendaByDay(agendaItems), [agendaItems]);

  // Chip data for a month/week cell: types beyond what the calendar already
  // draws (schedule events, job spans), merged so money-due shares one chip.
  const agendaChipsFor = useCallback((dateKey: string) => {
    const extras = (agendaByDate.get(dateKey) || []).filter(i => AGENDA_EXTRA_TYPES.includes(i.type));
    if (extras.length === 0) return [];
    const groups = new Map<string, { icon: string; chip: string; count: number; titles: string[] }>();
    for (const it of extras) {
      const key = it.type === 'invoice_due' || it.type === 'payable' ? 'money' : it.type;
      const meta = AGENDA_META[it.type];
      const g = groups.get(key) || {
        icon: key === 'money' ? 'attach_money' : meta.icon,
        chip: meta.chip,
        count: 0,
        titles: [],
      };
      g.count++;
      g.titles.push(it.label);
      groups.set(key, g);
    }
    return [...groups.values()];
  }, [agendaByDate]);

  // Persist view mode and drag-lock preference across reloads
  useEffect(() => {
    localStorage.setItem('calendarViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('calendarDragLocked', String(isDragLocked));
  }, [isDragLocked]);

  // Keyboard shortcuts: ←/→ navigate, T = today, D/W/M/Y/G switch views
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'ArrowLeft': navigateDate('prev'); break;
        case 'ArrowRight': navigateDate('next'); break;
        case 't': case 'T': setCurrentDate(new Date()); break;
        case 'd': case 'D': setViewMode('Day'); break;
        case 'w': case 'W': setViewMode('Week'); break;
        case 'm': case 'M': setViewMode('Month'); break;
        case 'y': case 'Y': setViewMode('Year'); break;
        case 'g': case 'G': setViewMode('Gantt'); break;
        default: return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentDate, viewMode]);

  const setSelectedEvent = async (event: ScheduleEvent | Job) => {
    const scheduleEvent = event as ScheduleEvent;
    setEditingEvent(scheduleEvent);
    
    // Load full job details for the form
    try {
      const jobId = scheduleEvent.job_id || scheduleEvent.jobId;
      if (jobId) {
        const details = await getJobById(jobId.toString());
        setEditingJobDetails(details);
      }
    } catch (error) {
      console.error('Failed to load job details:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load data with individual error handling
      const [jobsResult, clientsResult, crewsResult] = await Promise.allSettled([
        getJobs({ limit: 1000 }),
        getClients(),
        getCrews(),
      ]);
      
      const jobsData = jobsResult.status === 'fulfilled' ? jobsResult.value : { jobs: [] };
      const clientsData = clientsResult.status === 'fulfilled' ? clientsResult.value : [];
      const crewsData = crewsResult.status === 'fulfilled' ? crewsResult.value : [];
      
      if (jobsResult.status === 'rejected') console.error('Failed to load jobs:', jobsResult.reason);
      if (clientsResult.status === 'rejected') console.error('Failed to load clients:', clientsResult.reason);
      if (crewsResult.status === 'rejected') console.error('Failed to load crews:', crewsResult.reason);
      
      
      setAllJobs(jobsData.jobs || []);
      setJobs(jobsData.jobs || []);
      setClients(clientsData || []);
      setCrews(crewsData || []);
      
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScheduleEvents = async () => {
    try {
      // Don't load if we don't have jobs data yet
      if (allJobs.length === 0) {
        return;
      }
      
      
      const params: any = {};
      
      // Calculate date range based on view mode
      let calcStartDate = startDate;
      let calcEndDate = endDate;
      
      if (!startDate || !endDate) {
        if (viewMode === 'Day') {
          calcStartDate = currentDate.toISOString().split('T')[0];
          calcEndDate = calcStartDate;
        } else if (viewMode === 'Week') {
          const weekStart = new Date(currentDate);
          weekStart.setDate(weekStart.getDate() - weekStart.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          calcStartDate = weekStart.toISOString().split('T')[0];
          calcEndDate = weekEnd.toISOString().split('T')[0];
        } else if (viewMode === 'Month') {
          const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
          const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
          calcStartDate = monthStart.toISOString().split('T')[0];
          calcEndDate = monthEnd.toISOString().split('T')[0];
        } else if (viewMode === 'Year') {
          const yearStart = new Date(currentDate.getFullYear(), 0, 1);
          const yearEnd = new Date(currentDate.getFullYear(), 11, 31);
          calcStartDate = yearStart.toISOString().split('T')[0];
          calcEndDate = yearEnd.toISOString().split('T')[0];
        }
      }
      
      if (selectedJobId) params.jobId = selectedJobId;
      if (calcStartDate) params.startDate = calcStartDate;
      if (calcEndDate) params.endDate = calcEndDate;
      
      // Load schedule events (tasks)
      let events: ScheduleEvent[] = [];
      
      // If client is selected but no job, filter events by client's jobs
      if (selectedClientId && !selectedJobId) {
        const clientJobs = allJobs.filter(j => j.clientId?.toString() === selectedClientId);
        if (clientJobs.length > 0) {
          const allEvents = await Promise.all(
            clientJobs.map(job => getScheduleEvents({ ...params, jobId: job.id.toString() }))
          );
          events = allEvents.flat() || [];
        } else {
          // No jobs for this client
          events = [];
        }
      } else {
        // Load events normally
        events = await getScheduleEvents(params);
      }
      
      // Group multi-day tasks that were created as separate DB entries
      // Tasks with same job_id, start_time, and notes are considered the same task series
      const taskGroups = new Map<string, ScheduleEvent[]>();
      (events || []).forEach(event => {
        // Create a key based on job_id, start_time, and notes to identify task series
        const groupKey = `${event.job_id || 'no-job'}_${event.start_time || event.startTime || 'no-time'}_${event.notes || 'no-notes'}`;
        if (!taskGroups.has(groupKey)) {
          taskGroups.set(groupKey, []);
        }
        taskGroups.get(groupKey)!.push(event);
      });
      
      // For each group, keep only the earliest date entry and set its end_date
      const groupedEvents: ScheduleEvent[] = [];
      taskGroups.forEach((group) => {
        if (group.length === 1) {
          // Single event, keep as is
          groupedEvents.push(group[0]);
        } else {
          // Multiple events - sort by date and keep earliest, set end_date to latest
          group.sort((a, b) => {
            const dateA = a.scheduled_date || a.scheduledDate;
            const dateB = b.scheduled_date || b.scheduledDate;
            return dateA.localeCompare(dateB);
          });
          
          const earliest = group[0];
          const latest = group[group.length - 1];
          const latestDate = latest.scheduled_date || latest.scheduledDate;
          
          
          // Create consolidated event with start and end dates
          groupedEvents.push({
            ...earliest,
            end_date: latestDate,
            endDate: latestDate
          } as ScheduleEvent);
        }
      });
      
      events = groupedEvents;
      
      // Expand subtasks into separate pseudo-events for calendar display
      const expandedEvents: ScheduleEvent[] = [...events];
      let subtaskCount = 0;
      events.forEach(event => {
        if ((event as any).subtasks && Array.isArray((event as any).subtasks) && (event as any).subtasks.length > 0) {
          subtaskCount += (event as any).subtasks.length;
          (event as any).subtasks.forEach((subtask: any) => {
            // Create a pseudo-event for the subtask
            const subtaskEvent: ScheduleEvent = {
              id: `subtask-${event.id}-${subtask.id}` as any,
              job_id: event.job_id,
              jobId: event.jobId,
              job_number: event.job_number,
              jobNumber: event.jobNumber,
              job_title: event.job_title,
              jobTitle: event.jobTitle,
              client_name: event.client_name,
              clientName: event.clientName,
              site_address: event.site_address,
              siteAddress: event.siteAddress,
              crew_id: event.crew_id,
              crewId: event.crewId,
              crew_name: event.crew_name,
              crewName: event.crewName,
              scheduled_date: event.scheduled_date || event.scheduledDate,
              scheduledDate: event.scheduledDate || event.scheduled_date,
              end_date: event.end_date || event.endDate,
              endDate: event.endDate || event.end_date,
              start_time: subtask.start_time || subtask.startTime || event.start_time || event.startTime,
              startTime: subtask.startTime || subtask.start_time || event.startTime || event.start_time,
              end_time: subtask.end_time || subtask.endTime || event.end_time || event.endTime,
              endTime: subtask.endTime || subtask.end_time || event.endTime || event.end_time,
              status: subtask.status || event.status,
              notes: subtask.title || subtask.description || event.notes,
              parent_task_id: event.id,
              parentTaskId: event.id,
            } as ScheduleEvent;
            expandedEvents.push(subtaskEvent);
          });
        }
      });
      
      events = expandedEvents;
      
      // Ensure tasks have default times if not set (so they appear in timeline)
      let tasksWithoutTime = 0;
      events = (events || []).map(event => {
        // If it's a task (has notes/task name) but no time, assign default work hours
        if ((event.notes || (event as any).task_name) && !event.start_time && !event.startTime) {
          tasksWithoutTime++;
          return {
            ...event,
            start_time: '08:00:00',
            startTime: '08:00:00',
            end_time: '12:00:00',
            endTime: '12:00:00',
          };
        }
        return event;
      });
      
      
      // Convert jobs to calendar events for display
      const jobEvents: ScheduleEvent[] = [];
      const jobsToShow = selectedJobId 
        ? allJobs.filter(j => j.id.toString() === selectedJobId)
        : selectedClientId
        ? allJobs.filter(j => j.clientId?.toString() === selectedClientId)
        : allJobs;
      
      jobsToShow.forEach(job => {
        if (job.startDate) {
          // Parse job dates in local timezone
          const jobStartParts = job.startDate.split('T')[0].split('-');
          const jobStart = new Date(
            parseInt(jobStartParts[0]),
            parseInt(jobStartParts[1]) - 1,
            parseInt(jobStartParts[2])
          );
          
          let jobEnd = jobStart;
          if (job.estimatedEndDate) {
            const jobEndParts = job.estimatedEndDate.split('T')[0].split('-');
            jobEnd = new Date(
              parseInt(jobEndParts[0]),
              parseInt(jobEndParts[1]) - 1,
              parseInt(jobEndParts[2])
            );
          }
          
          if (calcStartDate && calcEndDate) {
            // Parse range dates in local timezone
            const rangeStartParts = calcStartDate.split('-');
            const rangeStart = new Date(
              parseInt(rangeStartParts[0]),
              parseInt(rangeStartParts[1]) - 1,
              parseInt(rangeStartParts[2])
            );
            rangeStart.setHours(0, 0, 0, 0);
            
            const rangeEndParts = calcEndDate.split('-');
            const rangeEnd = new Date(
              parseInt(rangeEndParts[0]),
              parseInt(rangeEndParts[1]) - 1,
              parseInt(rangeEndParts[2])
            );
            rangeEnd.setHours(23, 59, 59, 999);
            
            const jobStartCompare = new Date(jobStart);
            jobStartCompare.setHours(0, 0, 0, 0);
            
            const jobEndCompare = new Date(jobEnd);
            jobEndCompare.setHours(23, 59, 59, 999);
            
            // Skip if job is completely outside date range
            // Job is visible if it overlaps with the range at all
            if (jobEndCompare < rangeStart || jobStartCompare > rangeEnd) {
              return;
            }
            
          }
          
          // Create a synthetic event from job data
          // Jobs don't have specific time slots - only tasks and subtasks do
          jobEvents.push({
            id: `job-${job.id}` as any,
            job_id: job.id,
            job_number: job.jobNumber,
            job_title: job.title,
            client_name: job.client,
            scheduled_date: job.startDate,
            scheduledDate: job.startDate,
            end_date: job.estimatedEndDate || job.startDate,
            endDate: job.estimatedEndDate || job.startDate,
            // No time slots for jobs - they span entire work days
            start_time: undefined,
            startTime: undefined,
            end_time: undefined,
            endTime: undefined,
            status: job.status || 'scheduled',
            crew_id: undefined,
            crewId: undefined,
            crew_name: job.crew,
            crewName: job.crew,
            notes: `Job Duration: ${job.startDate} to ${job.estimatedEndDate || job.startDate}`,
          } as ScheduleEvent);
        }
      });
      
      // Combine schedule events and job events
      // Note: Events will be filtered when rendering to not display on weekends/holidays
      const allEvents = [...(events || []), ...jobEvents];
      
      // Deduplicate events by ID (for multi-day events that may appear multiple times)
      const uniqueEventsMap = new Map<number | string, ScheduleEvent>();
      allEvents.forEach(event => {
        const eventId = typeof event.id === 'string' ? event.id : event.id;
        if (!uniqueEventsMap.has(eventId)) {
          uniqueEventsMap.set(eventId, event);
        }
      });
      
      const deduplicatedEvents = Array.from(uniqueEventsMap.values());
      setScheduleEvents(deduplicatedEvents);
    } catch (error) {
      console.error('Failed to load schedule events:', error);
    }
  };

  const handleDateDoubleClick = (_date: Date) => {
    // Removed - Schedule event form has been removed
  };

  // Calculate dynamic task status based on dates and completion
  const getTaskStatus = (event: ScheduleEvent): string => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const startDate = new Date((event.scheduled_date || event.scheduledDate).split('T')[0]);
    const endDate = new Date(((event as any).end_date || (event as any).endDate || event.scheduled_date || event.scheduledDate).split('T')[0]);
    
    // Check if all subtasks are complete
    const subtasks = (event as any).subtasks;
    let allSubtasksComplete = false;
    
    if (subtasks && subtasks.length > 0) {
      allSubtasksComplete = subtasks.every((subtask: any) => {
        if (!subtask.checklistItems || subtask.checklistItems.length === 0) {
          return false; // Subtask with no checklist items is not complete
        }
        return subtask.checklistItems.every((item: any) => item.checked);
      });
      
      if (allSubtasksComplete) {
        return 'completed';
      }
    }
    
    // If end date is before current date, it's overdue
    if (endDate < now) {
      return 'overdue';
    }
    
    // If current date is within start/end range, it's in_progress (pending)
    if (now >= startDate && now <= endDate) {
      return 'in_progress';
    }
    
    // If start date is in the future, it's scheduled
    if (startDate > now) {
      return 'scheduled';
    }
    
    // Fallback to stored status
    return event.status || 'scheduled';
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (isDragLocked) return;
    const draggedEvent = scheduleEvents.find(e => e.id.toString() === event.active.id);
    setActiveEvent(draggedEvent || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (isDragLocked || !over || !active) {
      setActiveEvent(null);
      return;
    }

    const eventId = parseInt(active.id.toString());
    const dropData = over.data.current as { date?: string; hour?: number } | undefined;

    if (dropData && dropData.date) {
      // Check if trying to drop on weekend
      const dropDate = new Date(dropData.date);
      const dayOfWeek = dropDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        toast.error('Cannot schedule events on weekends (Saturday or Sunday). Please select a weekday.');
        setActiveEvent(null);
        return;
      }

      const draggedEvent = scheduleEvents.find(e => e.id === eventId);
      if (!draggedEvent) {
        setActiveEvent(null);
        return;
      }

      try {
        let newStartTime = draggedEvent.start_time || draggedEvent.startTime;
        let newEndTime = draggedEvent.end_time || draggedEvent.endTime;

        // If hour is specified (for Day and Week views), update time
        if (dropData.hour !== undefined) {
          newStartTime = `${String(dropData.hour).padStart(2, '0')}:00:00`;
          
          // Calculate duration and set end time
          if (draggedEvent.start_time && draggedEvent.end_time) {
            const oldStart = draggedEvent.start_time.split(':');
            const oldEnd = draggedEvent.end_time.split(':');
            const duration = parseInt(oldEnd[0]) - parseInt(oldStart[0]);
            const newEndHour = dropData.hour + duration;
            newEndTime = `${String(newEndHour).padStart(2, '0')}:00:00`;
          } else {
            newEndTime = `${String(dropData.hour + 2).padStart(2, '0')}:00:00`;
          }
        }

        await updateScheduleEvent(eventId, {
          scheduledDate: dropData.date,
          startTime: newStartTime,
          endTime: newEndTime,
        });

        // Refresh events
        await loadScheduleEvents();
      } catch (error) {
        console.error('Failed to update schedule event:', error);
      }
    }

    setActiveEvent(null);
  };

  const getWeekDates = useMemo(() => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [currentDate]);

  // Pre-calculate events for all dates to avoid repeated filtering
  // IMPORTANT: Only map events to their START date, not continuation dates
  // This ensures each task appears once regardless of how the date is set (URL, navigation, etc.)
  const eventsByDate = useMemo(() => {
    const map = new Map<string, ScheduleEvent[]>();
    
    
    scheduleEvents.forEach(event => {
      const eventStartRaw = event.scheduled_date || event.scheduledDate;
      if (!eventStartRaw) return;
      
      // Only add event to its start date
      const dateKey = eventStartRaw.split('T')[0];
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)!.push(event);
    });
    
    
    return map;
  }, [scheduleEvents]);

  const getEventsForDate = useCallback((date: Date) => {
    // Don't show events on weekends or holidays
    const dayOfWeek = date.getDay();
    const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
    const isHolidayDay = isHoliday(date, holidays);
    
    if (isWeekendDay || isHolidayDay) {
      return [];
    }
    
    const dateKey = date.toISOString().split('T')[0];
    return eventsByDate.get(dateKey) || [];
  }, [eventsByDate, holidays]);



  const getEventsForMonth = (year: number, month: number, day: number) => {
    const date = new Date(year, month, day);
    
    // Don't show events on weekends or holidays
    const dayOfWeek = date.getDay();
    const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
    const isHolidayDay = isHoliday(date, holidays);
    
    if (isWeekendDay || isHolidayDay) {
      return [];
    }
    
    const checkDate = new Date(year, month, day);
    checkDate.setHours(0, 0, 0, 0);
    
    const filtered = scheduleEvents.filter(event => {
      const eventStartRaw = event.scheduled_date || event.scheduledDate;
      if (!eventStartRaw) return false;
      
      // Parse date in local timezone to avoid UTC conversion issues
      const eventStartParts = eventStartRaw.split('T')[0].split('-');
      const eventStartDate = new Date(
        parseInt(eventStartParts[0]), 
        parseInt(eventStartParts[1]) - 1, 
        parseInt(eventStartParts[2])
      );
      eventStartDate.setHours(0, 0, 0, 0);
      
      // Get end date (could be multi-day event)
      const eventEndRaw = (event as any).end_date || (event as any).endDate || eventStartRaw;
      const eventEndParts = eventEndRaw.split('T')[0].split('-');
      const eventEndDate = new Date(
        parseInt(eventEndParts[0]), 
        parseInt(eventEndParts[1]) - 1, 
        parseInt(eventEndParts[2])
      );
      eventEndDate.setHours(0, 0, 0, 0);
      
      // Check if the date falls within the event's date range
      return checkDate >= eventStartDate && checkDate <= eventEndDate;
    });
    return filtered;
  };

  const getEventTimeSlot = (event: ScheduleEvent) => {
    const startTime = event.start_time || event.startTime;
    if (!startTime) return null;
    const [hours, minutes] = startTime.split(':').map(Number);
    const timeInHours = hours + (minutes / 60);
    // Work hours: 7:30 AM (7.5) to 3:30 PM (15.5)
    return timeInHours >= 7.5 && timeInHours < 15.5 ? Math.floor(timeInHours - 7.5) : null;
  };

  // Helper to calculate event spans for week view (jobs without time slots)
  const getWeekEventSpans = useMemo(() => {
    const weekDates = getWeekDates;
    const eventSpans: Map<number | string, { event: ScheduleEvent; startIdx: number; endIdx: number; }> = new Map();
    const renderedEvents = new Set<number | string>();

    weekDates.forEach((date, _idx) => {
      const dayEvents = getEventsForDate(date);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      
      if (isWeekend) return;
      
      dayEvents.forEach(event => {
        if (renderedEvents.has(event.id)) return;
        
        const timeSlot = getEventTimeSlot(event);
        if (timeSlot !== null) return; // Skip time-specific events
        
        // Calculate span for this event
        const eventStartRaw = event.scheduled_date || event.scheduledDate;
        const eventEndRaw = (event as any).end_date || (event as any).endDate || eventStartRaw;
        
        const eventStartParts = eventStartRaw.split('T')[0].split('-');
        const eventStartDate = new Date(
          parseInt(eventStartParts[0]), 
          parseInt(eventStartParts[1]) - 1, 
          parseInt(eventStartParts[2])
        );
        eventStartDate.setHours(0, 0, 0, 0);
        
        const eventEndParts = eventEndRaw.split('T')[0].split('-');
        const eventEndDate = new Date(
          parseInt(eventEndParts[0]), 
          parseInt(eventEndParts[1]) - 1, 
          parseInt(eventEndParts[2])
        );
        eventEndDate.setHours(0, 0, 0, 0);
        
        // Find start and end indices in week
        let startIdx = -1;
        let endIdx = -1;
        
        weekDates.forEach((wd, wdIdx) => {
          const wdCopy = new Date(wd);
          wdCopy.setHours(0, 0, 0, 0);
          const isWeekendDay = wd.getDay() === 0 || wd.getDay() === 6;
          
          if (!isWeekendDay && wdCopy.getTime() >= eventStartDate.getTime() && wdCopy.getTime() <= eventEndDate.getTime()) {
            if (startIdx === -1) startIdx = wdIdx;
            endIdx = wdIdx;
          }
        });
        
        if (startIdx !== -1 && endIdx !== -1) {
          eventSpans.set(event.id, { event, startIdx, endIdx });
          renderedEvents.add(event.id);
        }
      });
    });
    
    return eventSpans;
  }, [getWeekDates, getEventsForDate, scheduleEvents, holidays]);

  const getEventDuration = (event: ScheduleEvent) => {
    const startTime = event.start_time || event.startTime;
    const endTime = event.end_time || event.endTime;
    if (!startTime || !endTime) return 2;
    
    const startHour = parseInt(startTime.split(':')[0]) + parseInt(startTime.split(':')[1]) / 60;
    const endHour = parseInt(endTime.split(':')[0]) + parseInt(endTime.split(':')[1]) / 60;
    return endHour - startHour;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return 'check_circle';
      case 'in_progress':
        return 'pending';
      case 'scheduled':
        return 'schedule';
      case 'cancelled':
        return 'cancel';
      case 'on_hold':
        return 'pause_circle';
      case 'overdue':
        return 'error';
      default:
        return 'radio_button_unchecked';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-700 bg-green-100 border-green-600';
      case 'in_progress':
        return 'text-blue-700 bg-blue-100 border-blue-600';
      case 'scheduled':
        return 'text-purple-700 bg-purple-100 border-purple-600';
      case 'cancelled':
        return 'text-red-700 bg-red-100 border-red-600';
      case 'on_hold':
        return 'text-amber-700 bg-amber-100 border-amber-600';
      case 'overdue':
        return 'text-red-900 bg-red-200 border-red-800';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-600';
    }
  };

  const getJobColor = (jobId: number | string) => {
    // Generate consistent color based on job ID with harmonized contrast
    const colors = [
      'bg-blue-100 border-l-4 border-blue-600 text-blue-800',
      'bg-emerald-100 border-l-4 border-emerald-600 text-emerald-800',
      'bg-violet-100 border-l-4 border-violet-600 text-violet-800',
      'bg-rose-100 border-l-4 border-rose-600 text-rose-800',
      'bg-indigo-100 border-l-4 border-indigo-600 text-indigo-800',
      'bg-cyan-100 border-l-4 border-cyan-600 text-cyan-800',
      'bg-orange-100 border-l-4 border-orange-600 text-orange-800',
      'bg-teal-100 border-l-4 border-teal-600 text-teal-800',
    ];
    const index = typeof jobId === 'number' ? jobId % colors.length : parseInt(jobId) % colors.length;
    return colors[index];
  };

  // Calculate non-overlapping positions for events
  const calculateEventPositions = (events: ScheduleEvent[]) => {
    if (events.length === 0) return [];
    if (events.length === 1) return [{ event: events[0], column: 0, totalColumns: 1 }];
    
    // Convert time to minutes for easier comparison
    const timeToMinutes = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + (minutes || 0);
    };

    // Sort events by start time, then by duration
    const sortedEvents = [...events].sort((a, b) => {
      const aStart = a.start_time || a.startTime || '00:00';
      const bStart = b.start_time || b.startTime || '00:00';
      if (aStart !== bStart) return aStart.localeCompare(bStart);
      
      const aDuration = getEventDuration(a);
      const bDuration = getEventDuration(b);
      return bDuration - aDuration; // Longer events first
    });

    // Track which columns are occupied at what times
    interface ColumnInfo {
      endTime: number; // in minutes
    }
    const columns: ColumnInfo[] = [];
    const eventColumns: { event: ScheduleEvent; column: number }[] = [];

    // Place each event in a column
    sortedEvents.forEach(event => {
      const startTime = event.start_time || event.startTime || '00:00';
      const endTime = event.end_time || event.endTime || '00:00';
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      
      // Find the first available column where this event doesn't overlap
      let column = 0;
      while (column < columns.length) {
        if (columns[column].endTime <= startMinutes) {
          // This column is free at this time
          break;
        }
        column++;
      }
      
      // Create new column if needed
      if (column >= columns.length) {
        columns.push({ endTime: endMinutes });
      } else {
        columns[column].endTime = endMinutes;
      }
      
      eventColumns.push({ event, column });
    });

    // Calculate total columns for each event based on overlaps
    const result = eventColumns.map(({ event, column }) => {
      const startTime = event.start_time || event.startTime || '00:00';
      const endTime = event.end_time || event.endTime || '00:00';
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      
      // Find max column number for overlapping events
      let maxColumn = column;
      eventColumns.forEach(({ event: otherEvent, column: otherColumn }) => {
        const otherStart = otherEvent.start_time || otherEvent.startTime || '00:00';
        const otherEnd = otherEvent.end_time || otherEvent.endTime || '00:00';
        const otherStartMinutes = timeToMinutes(otherStart);
        const otherEndMinutes = timeToMinutes(otherEnd);
        
        // Check if events overlap
        if (otherStartMinutes < endMinutes && otherEndMinutes > startMinutes) {
          maxColumn = Math.max(maxColumn, otherColumn);
        }
      });
      
      return {
        event,
        column,
        totalColumns: maxColumn + 1
      };
    });

    return result;
  };

  const handleEventResize = async (eventId: number | string, newEndTime: string) => {
    try {
      await updateScheduleEvent(eventId, { endTime: newEndTime });
      await loadScheduleEvents();
    } catch (error) {
      console.error('Failed to resize event:', error);
    }
  };

  const isPastDue = (event: ScheduleEvent) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const endDateStr = (event as any).end_date || (event as any).endDate || event.scheduled_date || event.scheduledDate;
    const endDate = new Date(endDateStr);
    endDate.setHours(0, 0, 0, 0);
    
    return endDate < today && event.status !== 'completed' && event.status !== 'cancelled';
  };

  const hasSchedulingConflict = (event: ScheduleEvent) => {
    const eventDate = event.scheduled_date || event.scheduledDate;
    const eventStartTime = event.start_time || event.startTime || '00:00:00';
    const eventEndTime = event.end_time || event.endTime || '23:59:59';
    
    // Parse event date and time
    const eventStart = new Date(`${eventDate}T${eventStartTime}`);
    
    // Also check end_date for multi-day events
    const eventEndDate = (event as any).end_date || (event as any).endDate || eventDate;
    const finalEventEnd = new Date(`${eventEndDate}T${eventEndTime}`);
    
    // Determine if this is a job (no specific time slot) or a scheduled task
    const timeSlot = getEventTimeSlot(event);
    const isJob = timeSlot === null;
    
    // Only check weekend/holiday conflicts for scheduled tasks, not jobs
    if (!isJob) {
      // Check if event is on weekend or holiday
      const startDate = new Date(eventDate);
      const endDate = new Date(eventEndDate);
      
      // Check each day in the event's date range
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
        const onHoliday = isHoliday(d, holidays);
        
        if (isWeekendDay || onHoliday) {
          return true;
        }
      }
    }
    
    // Skip crew conflict check if no crew assigned
    if (!event.crew_id && !event.crewId) return false;
    
    const crewId = event.crew_id || event.crewId;
    
    // Check for crew overlap with any other event
    return scheduleEvents.some(otherEvent => {
      // Skip same event
      if (otherEvent.id === event.id) return false;
      
      // Only check events with the same crew
      if ((otherEvent.crew_id || otherEvent.crewId) !== crewId) return false;
      
      // Skip cancelled events
      if (otherEvent.status === 'cancelled' || event.status === 'cancelled') return false;
      
      // Parse other event dates and times
      const otherDate = otherEvent.scheduled_date || otherEvent.scheduledDate;
      const otherStartTime = otherEvent.start_time || otherEvent.startTime || '00:00:00';
      const otherEndTime = otherEvent.end_time || otherEvent.endTime || '23:59:59';
      
      const otherStart = new Date(`${otherDate}T${otherStartTime}`);
      const otherEndDate = (otherEvent as any).end_date || (otherEvent as any).endDate || otherDate;
      const otherEnd = new Date(`${otherEndDate}T${otherEndTime}`);
      
      // Check if time ranges overlap (including multi-day events)
      // Events overlap if: start1 < end2 AND end1 > start2
      return eventStart < otherEnd && finalEventEnd > otherStart;
    });
  };

  const getPastDueTasks = () => {
    return scheduleEvents.filter(event => isPastDue(event));
  };

  const getConflictingTasks = () => {
    const conflicts = new Set<number | string>();
    scheduleEvents.forEach(event => {
      if (hasSchedulingConflict(event)) {
        conflicts.add(event.id);
      }
    });
    return scheduleEvents.filter(event => conflicts.has(event.id));
  };

  // Get conflict reasons for an event
  const getConflictReasons = (event: ScheduleEvent): string[] => {
    const reasons: string[] = [];
    const eventDate = event.scheduled_date || event.scheduledDate;
    const eventEndDate = (event as any).end_date || (event as any).endDate || eventDate;
    
    const startDate = new Date(eventDate);
    const endDate = new Date(eventEndDate);
    
    // Determine if this is a job (no specific time slot) or a scheduled task
    const timeSlot = getEventTimeSlot(event);
    const isJob = timeSlot === null;
    
    // Only check weekend/holiday conflicts for scheduled tasks, not jobs
    if (!isJob) {
      // Check for weekends and holidays
      const weekendDays: string[] = [];
      const holidayDays: string[] = [];
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
        const holiday = isHoliday(d, holidays);
        
        if (isWeekendDay) {
          weekendDays.push(d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }));
        }
        if (holiday) {
          holidayDays.push(`${holiday.name} (${d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })})`);
        }
      }
      
      if (weekendDays.length > 0) {
        reasons.push(`Weekend: ${weekendDays.join(', ')}`);
      }
      if (holidayDays.length > 0) {
        reasons.push(`Holiday: ${holidayDays.join(', ')}`);
      }
    }
    
    // Check for crew conflicts
    const conflictingEvents = getConflictingEventsFor(event);
    if (conflictingEvents.length > 0) {
      reasons.push(`Crew overlap with ${conflictingEvents.length} other task(s)`);
    }
    
    return reasons;
  };

  // Get conflicting events for a specific event (for detailed display)
  const getConflictingEventsFor = (event: ScheduleEvent) => {
    if (!event.crew_id && !event.crewId) return [];
    
    const crewId = event.crew_id || event.crewId;
    const eventDate = event.scheduled_date || event.scheduledDate;
    const eventStartTime = event.start_time || event.startTime || '00:00:00';
    const eventEndTime = event.end_time || event.endTime || '23:59:59';
    
    const eventStart = new Date(`${eventDate}T${eventStartTime}`);
    const eventEndDate = (event as any).end_date || (event as any).endDate || eventDate;
    const finalEventEnd = new Date(`${eventEndDate}T${eventEndTime}`);
    
    return scheduleEvents.filter(otherEvent => {
      if (otherEvent.id === event.id) return false;
      if ((otherEvent.crew_id || otherEvent.crewId) !== crewId) return false;
      if (otherEvent.status === 'cancelled' || event.status === 'cancelled') return false;
      
      const otherDate = otherEvent.scheduled_date || otherEvent.scheduledDate;
      const otherStartTime = otherEvent.start_time || otherEvent.startTime || '00:00:00';
      const otherEndTime = otherEvent.end_time || otherEvent.endTime || '23:59:59';
      
      const otherStart = new Date(`${otherDate}T${otherStartTime}`);
      const otherEndDate = (otherEvent as any).end_date || (otherEvent as any).endDate || otherDate;
      const otherEnd = new Date(`${otherEndDate}T${otherEndTime}`);
      
      return eventStart < otherEnd && finalEventEnd > otherStart;
    });
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'Week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewMode === 'Month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'Day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'Year') {
      newDate.setFullYear(newDate.getFullYear() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const selectedJob = jobs.find(job => job.id.toString() === selectedJobId);
  const selectedClient = clients.find(client => client.id === selectedClientId);

  const isViewingToday = currentDate.toDateString() === new Date().toDateString();

  const getISOWeek = (d: Date) => {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  const toDateInputValue = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary"></div>
        <p className="text-sm font-medium text-text-light/60">Loading calendar…</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-white relative" style={{ zIndex: 10 }}>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          {/* Date Navigation */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigateDate('prev')}
              aria-label="Previous period"
              title="Previous (←)"
              className="p-2 hover:bg-gray-50 rounded"
            >
              <span className="material-symbols-outlined text-text-light">chevron_left</span>
            </button>
            <div className="flex flex-col items-center min-w-[250px]">
              <h3 className="text-lg font-bold text-text-light text-center">
                {viewMode === 'Day' 
                  ? currentDate.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                  : viewMode === 'Week'
                  ? (() => {
                      const start = new Date(currentDate);
                      start.setDate(start.getDate() - start.getDay());
                      const end = new Date(start);
                      end.setDate(start.getDate() + 6);
                      return `Week ${getISOWeek(start)} · ${start.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                    })()
                  : viewMode === 'Year'
                  ? currentDate.getFullYear().toString()
                  : currentDate.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })
                }
              </h3>
              {viewMode === 'Day' && (() => {
                const holiday = isHoliday(currentDate, holidays);
                const wx = forecastByDate.get(ymdLocal(currentDate));
                return (holiday || wx) ? (
                  <div className="flex items-center gap-3 mt-1">
                    {holiday && (
                      <div className="flex items-center gap-1 text-sm text-red-600">
                        <span className="material-symbols-outlined text-base">celebration</span>
                        <span className="font-semibold">STAT · {holiday.name}</span>
                      </div>
                    )}
                    {wx && (
                      <div
                        className="flex items-center gap-1 text-sm text-gray-500"
                        title={`${wx.t_max}° / ${wx.t_min}°C · ${wx.precip_prob}% chance of precipitation (${wx.precip_mm}mm)`}
                      >
                        <span className="material-symbols-outlined text-base text-gray-400">{weatherIcon(wx.code)}</span>
                        <span className="font-semibold text-gray-600">{wx.t_max}°/{wx.t_min}°</span>
                        {wx.precip_prob >= 20 && <span className="font-medium text-blue-500">{wx.precip_prob}%</span>}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
            <button
              onClick={() => navigateDate('next')}
              aria-label="Next period"
              title="Next (→)"
              className="p-2 hover:bg-gray-50 rounded"
            >
              <span className="material-symbols-outlined text-text-light">chevron_right</span>
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              title="Jump to today (T)"
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                isViewingToday
                  ? 'bg-primary/10 text-primary cursor-default'
                  : 'bg-primary text-white hover:bg-primary/90'
              }`}
            >
              Today
            </button>
            <input
              type="date"
              value={toDateInputValue(currentDate)}
              onChange={(e) => {
                if (!e.target.value) return;
                const [y, m, d] = e.target.value.split('-').map(Number);
                setCurrentDate(new Date(y, m - 1, d));
              }}
              title="Jump to a specific date"
              aria-label="Jump to date"
              className="px-2 py-2 rounded text-sm font-medium bg-gray-50 border border-gray-200 text-text-light focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
          
          {/* Lock/Unlock Drag and Drop Button */}
          <button
            onClick={() => setIsDragLocked(!isDragLocked)}
            aria-pressed={!isDragLocked}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border flex items-center gap-1.5 transition-colors ${
              isDragLocked
                ? 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                : 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
            }`}
            title={isDragLocked ? 'Locked — click to enable drag-and-drop rescheduling' : 'Unlocked — drag events to reschedule. Click to lock.'}
          >
            <span className="material-symbols-outlined text-base">
              {isDragLocked ? 'lock' : 'lock_open'}
            </span>
            {isDragLocked ? 'Locked' : 'Unlocked'}
          </button>

          {/* Calendar subscription (Google / iCloud / Outlook) */}
          <div className="px-1.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 flex items-center">
            <CalendarSubscribe compact />
          </div>

          {/* Hours-logging overlay toggle — turns the month grid into a timesheet */}
          <button
            onClick={() => setHoursMode(v => !v)}
            title="Toggle hours overlay — double-click a day to log start/finish hours for the crew"
            className={`px-3 py-1.5 text-sm font-medium rounded-lg flex items-center gap-1.5 shrink-0 border ${
              hoursMode
                ? 'bg-primary text-white border-primary shadow-sm'
                : 'bg-gray-50 text-text-light/70 border-gray-200 hover:bg-white'
            }`}
          >
            <span className="material-symbols-outlined text-base">schedule</span>
            Hours
          </button>

          {/* View Mode Selector */}
          <div className="flex items-center border border-gray-200 rounded-lg p-1 bg-gray-50 overflow-x-auto max-w-full">
            {(['Day', 'Week', 'Month', 'Year', 'Gantt'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                title={`${mode} view (${mode[0]})`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 shrink-0 ${
                  viewMode === mode
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-light/70 hover:bg-white'
                }`}
              >
                <span className="material-symbols-outlined text-base">
                  {mode === 'Day' && 'calendar_view_day'}
                  {mode === 'Week' && 'calendar_view_week'}
                  {mode === 'Month' && 'calendar_view_month'}
                  {mode === 'Year' && 'date_range'}
                  {mode === 'Gantt' && 'view_kanban'}
                </span>
                {mode}
              </button>
            ))}
          </div>
        </div>
        
        {/* Client and Job Selectors */}
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col min-w-40 flex-1">
            <p className="text-base font-medium leading-normal pb-2 text-text-light">Select Client</p>
            <select 
              value={selectedClientId}
              onChange={(e) => {
                setSelectedClientId(e.target.value);
                // When client changes, reset job to show all jobs (empty string means all)
                setSelectedJobId('');
              }}
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-light focus:outline-0 focus:ring-1 border border-gray-200 bg-gray-50 focus:border-primary focus:ring-primary h-12 p-[15px] text-base font-normal leading-normal"
            >
              <option value="">All clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col min-w-40 flex-1">
            <p className="text-base font-medium leading-normal pb-2 text-text-light">Select Job/Project</p>
            <select 
              value={selectedJobId}
              onChange={(e) => {
                const newJobId = e.target.value;
                setSelectedJobId(newJobId);
                // Find job in allJobs to get correct client
                if (newJobId) {
                  const job = allJobs.find(j => j.id.toString() === newJobId);
                  if (job) {
                    // Set the client
                    if (job.clientId) {
                      setSelectedClientId(job.clientId.toString());
                    }
                    // Navigate to job's start date
                    if (job.startDate) {
                      const startDate = new Date(job.startDate);
                      setCurrentDate(startDate);
                    }
                  }
                }
              }}
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-text-light focus:outline-0 focus:ring-1 border border-gray-200 bg-gray-50 focus:border-primary focus:ring-primary h-12 p-[15px] text-base font-normal leading-normal"
              disabled={jobs.length === 0}
            >
              <option value="">
                {selectedClientId ? 'All jobs for this client' : 'All jobs'}
              </option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>
                  {job.jobNumber} - {job.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex h-full">
          {/* Calendar Views */}
          <div className="flex-1 overflow-auto">
            {/* View Info Bar */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const conflicts = getConflictingTasks().length;
                  const pastDue = getPastDueTasks().length;
                  return (
                    <>
                      {conflicts > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                          <span className="material-symbols-outlined text-sm">warning</span>
                          {conflicts} conflict{conflicts === 1 ? '' : 's'}
                        </span>
                      )}
                      {pastDue > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-red-200">
                          <span className="material-symbols-outlined text-sm">event_busy</span>
                          {pastDue} past due
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {(selectedClientId || selectedJobId) && (
                  <button
                    onClick={() => { setSelectedClientId(''); setSelectedJobId(''); }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                  >
                    <span className="material-symbols-outlined text-sm">filter_alt_off</span>
                    Clear filters
                  </button>
                )}
                {selectedClient && !selectedJobId && (
                  <span className="text-xs text-text-light/60">
                    Client: <span className="font-medium text-text-light">{selectedClient.name}</span>
                  </span>
                )}
                {selectedJob && (
                  <button
                    onClick={() => navigate(jobPath(selectedJob.clientId, selectedJob.id))}
                    className="text-xs font-medium text-primary hover:underline"
                    title="Open job details"
                  >
                    {selectedJob.jobNumber} — {selectedJob.title}
                  </button>
                )}
                <div className="text-sm text-text-light/60">
                  {scheduleEvents.length === 0
                    ? 'No events in this range'
                    : `${scheduleEvents.length} event${scheduleEvents.length === 1 ? '' : 's'} scheduled`}
                </div>
              </div>
            </div>

            {viewMode === 'Week' && (
              <div id="week-view" className="bg-white min-h-full">
                <div className="grid grid-cols-8 border-t border-l border-gray-200">
                  {/* Header Row */}
                  <div className="bg-gray-50 p-3 border-b border-r border-gray-200">
                    <p className="text-sm font-semibold text-text-light">Time</p>
                  </div>
                  {getWeekDates.map((date, idx) => {
                    const dayEvents = getEventsForDate(date);
                    const holiday = isHoliday(date, holidays);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isToday = date.toDateString() === new Date().toDateString();

                    // Count unique tasks that are scheduled on this day (not counting multi-day tasks multiple times)
                    const uniqueTasks = new Set<number | string>();
                    dayEvents.filter(event => {
                      const timeSlot = getEventTimeSlot(event);
                      return timeSlot !== null; // Tasks with specific time slots
                    }).forEach(event => {
                      const eventStartRaw = event.scheduled_date || event.scheduledDate;
                      const eventStartParts = eventStartRaw.split('T')[0].split('-');
                      const eventStartDate = new Date(
                        parseInt(eventStartParts[0]), 
                        parseInt(eventStartParts[1]) - 1, 
                        parseInt(eventStartParts[2])
                      );
                      eventStartDate.setHours(0, 0, 0, 0);
                      
                      const checkDate = new Date(date);
                      checkDate.setHours(0, 0, 0, 0);
                      
                      // Only count if this is the start date of the task
                      if (eventStartDate.getTime() === checkDate.getTime()) {
                        uniqueTasks.add(event.id);
                      }
                    });
                    const tasksCount = uniqueTasks.size;
                    
                    return (
                      <div key={idx} className={`border-b border-r border-gray-200 bg-white ${
                        isToday ? 'ring-2 ring-primary ring-inset' :
                        isWeekend ? 'bg-gray-50' :
                        holiday ? 'bg-red-50/50' : ''
                      }`}>
                        <div className={`p-3 text-center border-b border-gray-200 ${isToday ? 'bg-primary/10' : 'bg-gray-50'}`}>
                          <p className={`text-sm font-semibold ${isToday ? 'text-primary' : isWeekend ? 'text-text-light/50' : 'text-text-light'}`}>
                            {date.toLocaleDateString('en-CA', { weekday: 'long' })}
                          </p>
                          <p className="text-xs text-text-light/60">{formatDate(date)}</p>
                          {isToday && (
                            <span className="mt-1 inline-block rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-white">Today</span>
                          )}
                          {isWeekend && (
                            <div className="mt-1 text-xs text-text-light/40">
                              <span className="material-symbols-outlined text-xs">block</span>
                            </div>
                          )}
                          {holiday && (
                            <div className="flex items-center justify-center gap-1 mt-1 text-xs text-red-600">
                              <span className="material-symbols-outlined text-xs">celebration</span>
                              <span className="font-medium">STAT · {holiday.name}</span>
                            </div>
                          )}
                          {/* Weather for forecast-covered dates (CR #201) */}
                          {(() => {
                            const wx = forecastByDate.get(ymdLocal(date));
                            return wx ? (
                              <div
                                className="mt-1 flex items-center justify-center gap-1 text-[11px] leading-none text-gray-500"
                                title={`${wx.t_max}° / ${wx.t_min}°C · ${wx.precip_prob}% chance of precipitation (${wx.precip_mm}mm)`}
                              >
                                <span className="material-symbols-outlined text-[13px] leading-none text-gray-400">{weatherIcon(wx.code)}</span>
                                <span className="font-semibold text-gray-600">{wx.t_max}°/{wx.t_min}°</span>
                                {wx.precip_prob >= 20 && (
                                  <span className="flex items-center font-medium text-blue-500">
                                    <span className="material-symbols-outlined text-[11px] leading-none">water_drop</span>
                                    {wx.precip_prob}%
                                  </span>
                                )}
                              </div>
                            ) : null;
                          })()}
                          {/* Agenda chips (CR #206) */}
                          {(() => {
                            const chips = agendaChipsFor(ymdLocal(date));
                            if (chips.length === 0) return null;
                            return (
                              <div
                                className="mt-1 flex flex-wrap items-center justify-center gap-0.5 cursor-pointer"
                                onClick={() => { setCurrentDate(new Date(date)); setViewMode('Day'); }}
                                title={chips.flatMap(c => c.titles).join('\n') + '\nClick for the full day agenda'}
                              >
                                {chips.slice(0, 4).map((c, ci) => (
                                  <span key={ci} className={`inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold leading-none ring-1 ${c.chip}`}>
                                    <span className="material-symbols-outlined text-[11px] leading-none">{c.icon}</span>
                                    {c.count}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>

                        {!isWeekend && !holiday && tasksCount === 0 && (
                          <div className="p-2 text-center text-xs text-text-light/40 border-b border-gray-200">
                            {/* Placeholder for spacing */}
                            &nbsp;
                          </div>
                        )}
                        
                        {!isWeekend && tasksCount > 0 && (
                          <div className="p-2 bg-primary/5 border-b border-gray-200">
                            <span className="text-xs text-text-light/60">
                              {tasksCount} scheduled {tasksCount === 1 ? 'task' : 'tasks'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Multi-day Events Row */}
                  {(() => {
                    const eventSpans = getWeekEventSpans;
                    const weekDates = getWeekDates;
                    const renderedCells = new Set<number>();
                    
                    if (eventSpans.size === 0) return null;
                    
                    return (
                      <>
                        <div className="bg-gray-50 p-2 border-b border-r border-gray-200 flex items-center">
                          <p className="text-xs font-medium text-text-light/60">Jobs</p>
                        </div>
                        {weekDates.map((date, idx) => {
                          if (renderedCells.has(idx)) {
                            return null;
                          }
                          
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          
                          // Find event that starts at this index
                          let spanningEvent = null;
                          let span = 1;
                          
                          for (const [, eventData] of eventSpans.entries()) {
                            if (eventData.startIdx === idx) {
                              spanningEvent = eventData;
                              span = eventData.endIdx - eventData.startIdx + 1;
                              // Mark all spanned cells as rendered
                              for (let i = eventData.startIdx; i <= eventData.endIdx; i++) {
                                renderedCells.add(i);
                              }
                              break;
                            }
                          }
                          
                          if (spanningEvent) {
                            const event = spanningEvent.event;
                            const hasConflict = hasSchedulingConflict(event);
                            
                            return (
                              <div
                                key={idx}
                                className={`border-b border-r border-gray-200 p-2 ${getJobColor(event.job_id || event.jobId)} ${hasConflict ? 'ring-2 ring-amber-500 ring-inset' : ''} cursor-pointer hover:brightness-95 transition-all`}
                                style={{ gridColumn: `span ${span}` }}
                                onDoubleClick={() => setSelectedEvent(event)}
                                title={`${event.job_number || event.jobNumber} - ${event.job_title || event.jobTitle}\n${span > 1 ? `Spans ${span} days` : 'Single day'}${hasConflict ? '\n⚠️ CREW CONFLICT' : ''}\nDouble-click to edit`}
                              >
                                <div className="flex items-center gap-1.5">
                                  <span className={`material-symbols-outlined text-sm ${getStatusColor(event.status).split(' ')[0]}`}>
                                    {getStatusIcon(event.status)}
                                  </span>
                                  {hasConflict && (
                                    <span className="material-symbols-outlined text-sm text-amber-500" title="Crew conflict">
                                      warning
                                    </span>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">
                                      {event.job_number || event.jobNumber}
                                    </p>
                                    <p className="text-xs truncate opacity-90">
                                      {event.job_title || event.jobTitle}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (isWeekend) {
                            return (
                              <div key={idx} className="border-b border-r border-gray-200 bg-gray-50"></div>
                            );
                          } else {
                            return (
                              <div key={idx} className="border-b border-r border-gray-200 bg-white"></div>
                            );
                          }
                        })}
                      </>
                    );
                  })()}

                  {/* Time Slots */}
                  {(() => {
                    const weekDates = getWeekDates;
                    const renderedTasks = new Set<number | string>();
                    
                    return Array.from({ length: 8 }, (_, i) => i + 7.5).map((hour) => {
                      const currentSlot = Math.floor(hour - 7.5);
                      
                      // Collect unique tasks for this hour across all days (only once per task)
                      const tasksForThisHour: Array<{ event: ScheduleEvent; startDayIdx: number; endDayIdx: number }> = [];
                      
                      weekDates.forEach((date) => {
                        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                        if (isWeekend) return;
                        
                        const dayEvents = getEventsForDate(date);
                        dayEvents.forEach(event => {
                          const timeSlot = getEventTimeSlot(event);
                          if (timeSlot === currentSlot && !renderedTasks.has(event.id)) {
                            // Calculate the span of this task across days
                            const eventStartRaw = event.scheduled_date || event.scheduledDate;
                            const eventEndRaw = (event as any).end_date || (event as any).endDate || eventStartRaw;
                            
                            const eventStartDate = new Date(eventStartRaw.split('T')[0]);
                            const eventEndDate = new Date(eventEndRaw.split('T')[0]);
                            
                            let startDayIdx = -1;
                            let endDayIdx = -1;
                            
                            weekDates.forEach((wd, wdIdx) => {
                              const isWeekendDay = wd.getDay() === 0 || wd.getDay() === 6;
                              if (isWeekendDay) return;
                              
                              const wdDate = new Date(wd.toISOString().split('T')[0]);
                              if (wdDate >= eventStartDate && wdDate <= eventEndDate) {
                                if (startDayIdx === -1) startDayIdx = wdIdx;
                                endDayIdx = wdIdx;
                              }
                            });
                            
                            if (startDayIdx !== -1 && endDayIdx !== -1) {
                              tasksForThisHour.push({ event, startDayIdx, endDayIdx });
                              renderedTasks.add(event.id);
                            }
                          }
                          
                          // Also check for subtasks that have their own time/date ranges
                          if ((event as any).subtasks && Array.isArray((event as any).subtasks)) {
                            (event as any).subtasks.forEach((subtask: any) => {
                              const subtaskId = `subtask-${subtask.id}`;
                              if (renderedTasks.has(subtaskId as any)) return;
                              
                              // Use subtask's own time, or parent event's time if not specified
                              const subtaskStartTime = subtask.start_time || subtask.startTime || event.start_time || event.startTime;
                              if (!subtaskStartTime) return;
                              
                              const [hours, minutes] = subtaskStartTime.split(':').map(Number);
                              const timeInHours = hours + (minutes / 60);
                              const subtaskTimeSlot = timeInHours >= 7.5 && timeInHours < 15.5 ? Math.floor(timeInHours - 7.5) : null;
                              
                              if (subtaskTimeSlot === currentSlot) {
                                // Place subtask on its specific start date, or parent's date if not specified
                                const subtaskStartDateRaw = subtask.start_date || subtask.startDate || event.scheduled_date;
                                const subtaskStartDate = new Date(subtaskStartDateRaw.split('T')[0]);
                                
                                let subStartDayIdx = -1;
                                
                                weekDates.forEach((wd, wdIdx) => {
                                  const wdDate = new Date(wd.toISOString().split('T')[0]);
                                  if (wdDate.getTime() === subtaskStartDate.getTime()) {
                                    subStartDayIdx = wdIdx;
                                  }
                                });
                                
                                if (subStartDayIdx !== -1) {
                                  // Create a pseudo-event for the subtask
                                  const subtaskEvent = {
                                    ...event,
                                    id: subtaskId,
                                    notes: `${event.notes ? event.notes + ' - ' : ''}${subtask.title}`,
                                    start_time: subtaskStartTime,
                                    end_time: subtask.end_time || subtask.endTime || event.end_time || event.endTime,
                                    scheduled_date: subtaskStartDateRaw,
                                    subtasks: subtask.checklistItems || [], // Pass checklist items
                                    _isSubtask: true,
                                    _parentEvent: event
                                  };
                                  tasksForThisHour.push({ event: subtaskEvent as any, startDayIdx: subStartDayIdx, endDayIdx: subStartDayIdx });
                                  renderedTasks.add(subtaskId as any);
                                }
                              }
                            });
                          }
                        });
                      });
                      
                      return (
                      <React.Fragment key={hour}>
                        <div className="bg-gray-50/50 p-2 border-b border-r border-gray-200">
                          <p className="text-xs text-text-light/60">
                            {(() => {
                              const displayHour = Math.floor(hour);
                              const minutes = (hour % 1) * 60;
                              return `${displayHour <= 12 ? displayHour : displayHour - 12}:${minutes === 0 ? '00' : '30'} ${displayHour < 12 ? 'AM' : 'PM'}`;
                            })()}
                          </p>
                        </div>
                        
                        {/* Render cells with tasks */}
                        {weekDates.map((date, dayIdx) => {
                          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                          const dateStr = date.toISOString().split('T')[0];
                          
                          // Find tasks that start in this cell
                          const tasksInCell = tasksForThisHour.filter(t => t.startDayIdx === dayIdx);
                          
                          return (
                            <DroppableCell
                              key={`${hour}-${dayIdx}`}
                              id={isWeekend ? `weekend-${dateStr}-${hour}` : `week-${dateStr}-${hour}`}
                              date={dateStr}
                              hour={hour}
                              className={`relative p-2 border-b border-r border-gray-200 h-24 transition-all bg-white ${
                                isWeekend 
                                  ? 'bg-gray-50 cursor-not-allowed pointer-events-none' 
                                  : 'hover:border-primary hover:border-2 cursor-pointer'
                              }`}
                              onDoubleClick={!isWeekend ? () => handleDateDoubleClick(date) : undefined}
                            >
                              {tasksInCell.map(({ event, startDayIdx, endDayIdx }) => {
                                const spanDays = endDayIdx - startDayIdx + 1;
                                const duration = getEventDuration(event);
                                const heightInRem = duration * 6;
                                const hasConflict = hasSchedulingConflict(event);
                                const dynamicStatus = getTaskStatus(event);
                                
                                // Calculate width for multi-day span
                                const widthCalc = spanDays === 1 
                                  ? `calc(100% - 1rem)`
                                  : `calc(${spanDays * 100}% + ${(spanDays - 1)}px - 0.5rem)`;
                                
                                return (
                                  <DraggableEvent
                                    key={event.id}
                                    event={event}
                                    onResize={handleEventResize}
                                    onDoubleClick={setSelectedEvent}
                                    isDragLocked={isDragLocked}
                                  >
                                    <div
                                      className={`absolute rounded text-xs overflow-hidden ${getJobColor(event.job_id || event.jobId).replace('bg-', 'bg-opacity-50 bg-')} ${hasConflict ? 'ring-2 ring-amber-500' : ''} pointer-events-auto`}
                                      style={{
                                        left: '0.5rem',
                                        width: spanDays > 1 ? widthCalc : undefined,
                                        right: spanDays === 1 ? '0.5rem' : undefined,
                                        top: '0.5rem',
                                        height: `${heightInRem}rem`,
                                        minHeight: '3rem',
                                        zIndex: 50,
                                      }}
                                      title={`${event.job_number || event.jobNumber} - ${event.job_title || event.jobTitle}\n${event.client_name || event.clientName || ''}\nStatus: ${dynamicStatus}\n${event.start_time || event.startTime || ''} - ${event.end_time || event.endTime || ''} (${duration}h)\nSpans ${spanDays} ${spanDays === 1 ? 'day' : 'days'}${hasConflict ? '\n⚠️ CREW SCHEDULING CONFLICT' : ''}\nDouble-click to edit`}
                                    >
                                      <div className="p-1.5 h-full">
                                        <div className="flex items-center gap-1 mb-0.5">
                                          <span className={`material-symbols-outlined text-sm ${getStatusColor(dynamicStatus).split(' ')[0]}`}>
                                            {getStatusIcon(dynamicStatus)}
                                          </span>
                                          {hasConflict && (
                                            <span className="material-symbols-outlined text-sm text-amber-500" title="Crew conflict">
                                              warning
                                            </span>
                                          )}
                                          <p className="font-semibold truncate flex-1">
                                            {event.job_number || event.jobNumber}
                                          </p>
                                          {spanDays > 1 && (
                                            <span className="text-xs opacity-75 bg-white/20 px-1 rounded">
                                              {spanDays}d
                                            </span>
                                          )}
                                        </div>
                                        <p className="truncate text-xs opacity-90 mb-1">
                                          {event.job_title || event.jobTitle}
                                        </p>
                                        {event.notes && (
                                          <div className="mb-1 pb-1 border-b border-white/20">
                                            <p className="text-sm font-semibold opacity-90">
                                              📋 {event.notes}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Checklists for subtasks or regular subtasks list for parent tasks */}
                                        {(event as any)._isSubtask ? (
                                          // This is a subtask block - show its checklist items
                                          (event as any).subtasks && (event as any).subtasks.length > 0 && (
                                            <div className="text-xs space-y-0.5 mt-1 max-h-32 overflow-y-auto">
                                              {(event as any).subtasks.map((item: any, itemIdx: number) => (
                                                <label key={itemIdx} className="flex items-center gap-1 cursor-pointer hover:opacity-100">
                                                  <input
                                                    type="checkbox"
                                                    checked={item.checked || false}
                                                    onChange={async (e) => {
                                                      e.stopPropagation();
                                                      try {
                                                        await updateChecklistItem(item.id, e.target.checked);
                                                        await loadScheduleEvents();
                                                      } catch (error) {
                                                        console.error('Failed to update checklist:', error);
                                                      }
                                                    }}
                                                    className="w-3 h-3 rounded"
                                                  />
                                                  <span className={item.checked ? 'line-through opacity-60' : ''}>
                                                    {item.text}
                                                  </span>
                                                </label>
                                              ))}
                                            </div>
                                          )
                                        ) : (
                                          // This is a parent task - show subtasks inline only if they don't have their own times
                                          (event as any).subtasks && (event as any).subtasks.length > 0 && duration >= 2 && (
                                            <div className="text-xs space-y-1 mt-1 max-h-32 overflow-y-auto">
                                              {(event as any).subtasks.filter((st: any) => !st.start_time && !st.startTime).map((subtask: any, idx: number) => (
                                                <div key={idx} className="opacity-90">
                                                  <div className="font-medium mb-0.5">
                                                    • {subtask.title}
                                                  </div>
                                                  {subtask.checklistItems && subtask.checklistItems.length > 0 && (
                                                    <div className="ml-3 space-y-0.5">
                                                      {subtask.checklistItems.map((item: any, itemIdx: number) => (
                                                        <label key={itemIdx} className="flex items-center gap-1 cursor-pointer hover:opacity-100">
                                                          <input
                                                            type="checkbox"
                                                            checked={item.checked || false}
                                                            onChange={async (e) => {
                                                              e.stopPropagation();
                                                              try {
                                                                await updateChecklistItem(item.id, e.target.checked);
                                                                await loadScheduleEvents();
                                                              } catch (error) {
                                                                console.error('Failed to update checklist:', error);
                                                              }
                                                            }}
                                                            className="w-3 h-3 rounded"
                                                          />
                                                          <span className={item.checked ? 'line-through opacity-60' : ''}>
                                                            {item.text}
                                                          </span>
                                                        </label>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  </DraggableEvent>
                                );
                              })}
                            </DroppableCell>
                          );
                        })}
                      </React.Fragment>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {viewMode === 'Month' && (
              <div id="month-view" className="bg-white min-h-full">
                {hoursMode && missingHoursDays.length > 0 && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 m-3">
                    <span className="material-symbols-outlined text-amber-500 text-lg">event_busy</span>
                    <span className="text-sm text-amber-800">
                      <strong>{missingHoursDays.length}</strong> workday{missingHoursDays.length !== 1 ? 's' : ''} this month {missingHoursDays.length !== 1 ? 'have' : 'has'} no hours logged.
                    </span>
                    <button
                      onClick={() => setHoursModalDate(missingHoursDays[0])}
                      className="ml-auto text-xs font-semibold text-amber-800 border border-amber-300 rounded px-2 py-1 hover:bg-amber-100 whitespace-nowrap"
                    >
                      Log {hoursDateLabel(missingHoursDays[0]).replace(/, \d{4}$/, '')}
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-7 border-t border-l border-gray-200 relative">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <div key={day} className="bg-gray-50 p-3 border-b border-r border-gray-200 text-center">
                      <p className="text-sm font-semibold text-text-light">{day}</p>
                    </div>
                  ))}

                  {/* Calendar Days */}
                  {getMonthDays().map((day, idx) => {
                    const isCurrentMonth = day !== null;
                    const dayEvents = day ? getEventsForMonth(currentDate.getFullYear(), currentDate.getMonth(), day) : [];
                    const dateStr = day ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';
                    const cellDate = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                    const isWeekend = cellDate ? (cellDate.getDay() === 0 || cellDate.getDay() === 6) : false;
                    const isToday = cellDate ? cellDate.toDateString() === new Date().toDateString() : false;

                    // Separate jobs (no time slots) from tasks (with time slots)
                    const jobsForDay = dayEvents.filter(event => {
                      const timeSlot = getEventTimeSlot(event);
                      return timeSlot === null; // Jobs without specific time slots
                    });
                    
                    const tasksForDay = dayEvents.filter(event => {
                      const timeSlot = getEventTimeSlot(event);
                      return timeSlot !== null; // Tasks with specific time slots
                    });
                    
                    const handleDayClick = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (!cellDate || isWeekend) return;
                      setCurrentDate(cellDate);
                      setViewMode('Day');
                    };
                    
                    return (
                      <DroppableCell
                        key={idx}
                        id={isWeekend ? `weekend-${dateStr}-${idx}` : `month-${dateStr}-${idx}`}
                        date={dateStr}
                        className={`relative p-2 border-b border-r border-gray-200 h-36 overflow-hidden transition-all bg-white ${
                          !isCurrentMonth ? 'opacity-40' : ''
                        } ${isWeekend ? 'bg-gray-50' : ''} ${isToday ? 'ring-2 ring-primary ring-inset bg-primary/10' : ''}`}
                        onDoubleClick={cellDate ? () => { if (hoursMode) { setHoursModalDate(dateStr); } else if (!isWeekend) { handleDateDoubleClick(cellDate); } } : undefined}
                      >
                        {day && (
                          <>
                            <a
                              onClick={handleDayClick}
                              className={`absolute top-2 right-2 text-sm font-medium z-10 ${
                                isToday
                                  ? 'flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white cursor-pointer'
                                  : isWeekend
                                  ? 'text-text-light/50 cursor-not-allowed pointer-events-none'
                                  : 'text-text-light hover:text-primary hover:underline cursor-pointer'
                              }`}
                              title={isWeekend ? '' : 'Click to view this day'}
                            >
                              {day}
                            </a>
                            {isToday && (
                              <span className="absolute top-1.5 left-1/2 -translate-x-1/2 z-20 text-[10px] font-bold uppercase tracking-wider text-white bg-primary px-2 py-0.5 rounded-full shadow-sm pointer-events-none">
                                Today
                              </span>
                            )}
                            {(() => {
                              const holiday = cellDate ? isHoliday(cellDate, holidays) : null;
                              return holiday && (
                                <span
                                  className="absolute top-2 left-2 max-w-[calc(100%-2.75rem)] truncate text-[10px] bg-red-500/15 text-red-700 border border-red-200 px-1.5 py-0.5 rounded font-semibold pointer-events-none z-10"
                                  title={`Statutory holiday (BC): ${holiday.name}`}
                                >
                                  STAT · {holiday.name}
                                </span>
                              );
                            })()}
                            {/* Weather for forecast-covered dates (CR #201) */}
                            {!hoursMode && (() => {
                              const wx = forecastByDate.get(dateStr);
                              return wx ? (
                                <div
                                  className="absolute bottom-0.5 left-1 right-1 z-10 flex items-center justify-center gap-1 rounded bg-white/85 px-1 py-0.5 text-[10px] leading-none text-gray-500 pointer-events-none"
                                  title={`${wx.t_max}° / ${wx.t_min}°C · ${wx.precip_prob}% chance of precipitation (${wx.precip_mm}mm)`}
                                >
                                  <span className="material-symbols-outlined text-[12px] leading-none text-gray-400">{weatherIcon(wx.code)}</span>
                                  <span className="font-semibold text-gray-600">{wx.t_max}°/{wx.t_min}°</span>
                                  {wx.precip_prob >= 20 && (
                                    <span className="flex items-center font-medium text-blue-500">
                                      <span className="material-symbols-outlined text-[11px] leading-none">water_drop</span>
                                      {wx.precip_prob}%
                                    </span>
                                  )}
                                </div>
                              ) : null;
                            })()}
                            {/* Agenda chips (CR #206) — money due, tasks, talks, POs, quote expiry, agreements */}
                            {!hoursMode && (() => {
                              const chips = agendaChipsFor(dateStr);
                              if (chips.length === 0) return null;
                              return (
                                <div
                                  className="absolute bottom-[18px] left-1 right-1 z-30 flex flex-wrap gap-0.5 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (cellDate) { setCurrentDate(cellDate); setViewMode('Day'); }
                                  }}
                                  title={chips.flatMap(c => c.titles).join('\n') + '\nClick for the full day agenda'}
                                >
                                  {chips.slice(0, 4).map((c, ci) => (
                                    <span
                                      key={ci}
                                      className={`inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] font-semibold leading-none ring-1 ${c.chip}`}
                                    >
                                      <span className="material-symbols-outlined text-[11px] leading-none">{c.icon}</span>
                                      {c.count}
                                    </span>
                                  ))}
                                </div>
                              );
                            })()}

                            <div className="mt-8 space-y-1 relative z-20">
                              {/* Show Jobs as Subheadings */}
                              {!isWeekend && jobsForDay.length > 0 && (
                                <div className="mb-2 pb-1 border-b border-primary/20">
                                  {jobsForDay.slice(0, 2).map((job) => (
                                    <div 
                                      key={job.id}
                                      data-event
                                      className="text-xs cursor-pointer hover:bg-primary/10 rounded px-1 py-0.5 transition-colors mb-1"
                                      onDoubleClick={() => setSelectedEvent(job)}
                                      title={`${job.job_number || job.jobNumber} - ${job.job_title || job.jobTitle}\nDouble-click to edit`}
                                    >
                                      <div className="flex items-center gap-1">
                                        <span className={`material-symbols-outlined text-xs ${getStatusColor(job.status).split(' ')[0]}`}>
                                          {getStatusIcon(job.status)}
                                        </span>
                                        <span className="font-semibold text-primary truncate">
                                          {job.job_number || job.jobNumber}
                                        </span>
                                      </div>
                                      <p className="text-text-light/80 truncate text-[10px] pl-4">
                                        {job.job_title || job.jobTitle}
                                      </p>
                                    </div>
                                  ))}
                                  {jobsForDay.length > 2 && (
                                    <div className="text-[10px] text-text-light/60 px-1">
                                      +{jobsForDay.length - 2} more job{jobsForDay.length - 2 > 1 ? 's' : ''}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Show Tasks as Event Cards */}
                              {tasksForDay.slice(0, 2 - Math.min(jobsForDay.length, 2)).map((event) => {
                                const hasConflict = hasSchedulingConflict(event);
                                
                                return (
                                  <DraggableEvent 
                                    key={event.id} 
                                    event={event}
                                    onDoubleClick={setSelectedEvent}
                                    isDragLocked={isDragLocked}
                                  >
                                    <div
                                      data-event
                                      className={`px-1.5 py-0.5 rounded text-xs truncate flex items-center gap-1 ${getJobColor(event.job_id || event.jobId).replace('border-l-4', 'border-l-2')} ${hasConflict ? 'ring-1 ring-amber-500' : ''}`}
                                      title={`${event.job_number || event.jobNumber} - ${event.job_title || event.jobTitle}\nStatus: ${event.status}${hasConflict ? '\n⚠️ CREW CONFLICT' : ''}\nDouble-click to edit`}
                                    >
                                      <span className={`material-symbols-outlined text-xs ${getStatusColor(event.status).split(' ')[0]}`}>
                                        {getStatusIcon(event.status)}
                                      </span>
                                      {hasConflict && (
                                        <span className="material-symbols-outlined text-xs text-amber-500">
                                          warning
                                        </span>
                                      )}
                                      <span className="truncate">{event.job_number || event.jobNumber}</span>
                                    </div>
                                  </DraggableEvent>
                                );
                              })}
                              
                              {(jobsForDay.length + tasksForDay.length) > 3 && (
                                <div className="text-xs text-text-light/60 px-1.5 pointer-events-none">
                                  +{(jobsForDay.length + tasksForDay.length) - 3} more
                                </div>
                              )}
                            </div>
                            {/* Hours overlay: logged total or a "no hours" flag */}
                            {hoursMode && (() => {
                              const dayList = entriesByDate.get(dateStr) || [];
                              const total = dayList.reduce((s, e) => s + Number(e.hours), 0);
                              if (dayList.length > 0) {
                                return (
                                  <div className="absolute bottom-1 left-1 right-1 z-30 flex items-center justify-between rounded bg-primary/10 border border-primary/20 px-1.5 py-0.5 pointer-events-none">
                                    <span className="text-[10px] font-medium text-primary/80 flex items-center gap-0.5">
                                      <span className="material-symbols-outlined text-[11px]">schedule</span>{dayList.length}
                                    </span>
                                    <span className="text-[11px] font-bold text-primary tabular-nums">{total.toFixed(1)}h</span>
                                  </div>
                                );
                              }
                              if (missingHoursDays.includes(dateStr)) {
                                return (
                                  <div className="absolute bottom-1 left-1 right-1 z-30 flex items-center gap-0.5 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 pointer-events-none">
                                    <span className="material-symbols-outlined text-[11px]">warning</span> No hours
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </>
                        )}
                      </DroppableCell>
                    );
                  })}
                </div>
                {/* Legend for the agenda chip types (CR #206) */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-gray-200 bg-gray-50 px-4 py-2 text-[11px] text-text-light/70">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-text-light/50">Also on this calendar:</span>
                  {(['invoice_due', 'task', 'talk', 'po', 'quote_expiry', 'agreement'] as AgendaType[]).map(t => (
                    <span key={t} className="inline-flex items-center gap-1">
                      <span className={`inline-flex items-center rounded px-1 py-px ring-1 ${AGENDA_META[t].chip}`}>
                        <span className="material-symbols-outlined text-[11px] leading-none">{t === 'invoice_due' ? 'attach_money' : AGENDA_META[t].icon}</span>
                      </span>
                      {t === 'invoice_due' ? 'Money due (invoices & bills)' : AGENDA_META[t].name}
                    </span>
                  ))}
                  <span className="text-text-light/50">— click a chip for the day's full agenda</span>
                </div>
              </div>
            )}

            {viewMode === 'Gantt' && (
              <div id="gantt-view" className="bg-white min-h-full">
                <div className="space-y-4 p-6">
                  <h2 className="text-2xl font-bold text-text-light mb-4">Schedule Timeline</h2>
                  
                  {scheduleEvents.length > 0 ? (
                    <div className="space-y-3">
                      {/* Group events by jobs and tasks */}
                      {(() => {
                        // Separate jobs from tasks
                        const jobs = scheduleEvents.filter(event => {
                          const timeSlot = getEventTimeSlot(event);
                          return timeSlot === null;
                        });
                        
                        const tasks = scheduleEvents.filter(event => {
                          const timeSlot = getEventTimeSlot(event);
                          return timeSlot !== null;
                        });
                        
                        return (
                          <>
                            {/* Jobs Section */}
                            {jobs.length > 0 && (
                              <div className="mb-6">
                                <h3 className="text-lg font-semibold text-text-light mb-3 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-primary">work</span>
                                  Jobs ({jobs.length})
                                </h3>
                                <div className="space-y-2">
                                  {jobs.map((event) => {
                                    const hasConflict = hasSchedulingConflict(event);
                                    return (
                                      <div 
                                        key={event.id}
                                        className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all bg-white hover:border-primary hover:border-l-8 ${getJobColor(event.job_id || event.jobId).split('border-l-4')[1]} ${hasConflict ? 'ring-2 ring-amber-500' : ''}`}
                                        onDoubleClick={() => setSelectedEvent(event)}
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className={`material-symbols-outlined text-lg ${getStatusColor(event.status).split(' ')[0]}`}>
                                                {getStatusIcon(event.status)}
                                              </span>
                                              {hasConflict && (
                                                <span className="material-symbols-outlined text-lg text-amber-500" title="Scheduling conflict">
                                                  warning
                                                </span>
                                              )}
                                              <h4 className="font-semibold text-text-light">
                                                {event.job_number || event.jobNumber} - {event.job_title || event.jobTitle}
                                              </h4>
                                            </div>
                                            <p className="text-sm text-text-light/70">
                                              {event.client_name || event.clientName}
                                            </p>
                                          </div>
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(event.status)}`}>
                                            <span className="material-symbols-outlined text-xs">{getStatusIcon(event.status)}</span>
                                            {event.status.replace('_', ' ')}
                                          </span>
                                        </div>
                                        <div className="flex gap-4 text-sm text-text-light/60">
                                          <div className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-base">calendar_today</span>
                                            <span>
                                              {new Date(event.scheduled_date || event.scheduledDate).toLocaleDateString()}
                                            </span>
                                            {((event as any).end_date || (event as any).endDate) && (
                                              <span> → {new Date((event as any).end_date || (event as any).endDate).toLocaleDateString()}</span>
                                            )}
                                          </div>
                                          {(event.crew_name || event.crewName) && (
                                            <div className="flex items-center gap-1">
                                              <span className="material-symbols-outlined text-base">group</span>
                                              {event.crew_name || event.crewName}
                                            </div>
                                          )}
                                        </div>
                                        {event.notes && (
                                          <p className="mt-2 text-sm text-text-light/70 italic bg-gray-50 rounded p-2">
                                            {event.notes}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Tasks Section */}
                            {tasks.length > 0 && (
                              <div>
                                <h3 className="text-lg font-semibold text-text-light mb-3 flex items-center gap-2">
                                  <span className="material-symbols-outlined text-blue-600">task</span>
                                  Scheduled Tasks ({tasks.length})
                                </h3>
                                <div className="space-y-2">
                                  {tasks.map((event) => {
                                    const hasConflict = hasSchedulingConflict(event);
                                    return (
                                      <div 
                                        key={event.id}
                                        className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all bg-white hover:border-blue-600 hover:border-l-8 ${getJobColor(event.job_id || event.jobId).split('border-l-4')[1]} ${hasConflict ? 'ring-2 ring-amber-500' : ''}`}
                                        onDoubleClick={() => setSelectedEvent(event)}
                                      >
                                        <div className="flex justify-between items-start mb-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className={`material-symbols-outlined text-lg ${getStatusColor(event.status).split(' ')[0]}`}>
                                                {getStatusIcon(event.status)}
                                              </span>
                                              {hasConflict && (
                                                <span className="material-symbols-outlined text-lg text-amber-500" title="Scheduling conflict">
                                                  warning
                                                </span>
                                              )}
                                              <h4 className="font-semibold text-text-light">
                                                {event.job_number || event.jobNumber} - {event.job_title || event.jobTitle}
                                              </h4>
                                            </div>
                                            <p className="text-sm text-text-light/70">
                                              {event.client_name || event.clientName}
                                            </p>
                                          </div>
                                          <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(event.status)}`}>
                                            <span className="material-symbols-outlined text-xs">{getStatusIcon(event.status)}</span>
                                            {event.status.replace('_', ' ')}
                                          </span>
                                        </div>
                                        <div className="flex gap-4 text-sm text-text-light/60">
                                          <div className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-base">calendar_today</span>
                                            <span>
                                              {new Date(event.scheduled_date || event.scheduledDate).toLocaleDateString()}
                                            </span>
                                            {((event as any).end_date || (event as any).endDate) && 
                                             (event.scheduled_date || event.scheduledDate) !== ((event as any).end_date || (event as any).endDate) && (
                                              <span> → {new Date((event as any).end_date || (event as any).endDate).toLocaleDateString()}</span>
                                            )}
                                          </div>
                                          {(event.start_time || event.startTime) && (
                                            <div className="flex items-center gap-1">
                                              <span className="material-symbols-outlined text-base">schedule</span>
                                              {event.start_time || event.startTime} - {event.end_time || event.endTime}
                                            </div>
                                          )}
                                          {(event.crew_name || event.crewName) && (
                                            <div className="flex items-center gap-1">
                                              <span className="material-symbols-outlined text-base">group</span>
                                              {event.crew_name || event.crewName}
                                            </div>
                                          )}
                                        </div>
                                        {event.notes && (
                                          <p className="mt-2 text-sm text-text-light/70 italic bg-gray-50 rounded p-2">
                                            📋 {event.notes}
                                          </p>
                                        )}
                                        
                                        {/* Subtasks and Checklists */}
                                        {(event as any).subtasks && (event as any).subtasks.length > 0 && (
                                          <div className="mt-3 space-y-2 border-t border-gray-200 pt-3">
                                            <div className="flex items-center gap-2 text-sm font-medium text-text-light">
                                              <span className="material-symbols-outlined text-base">checklist</span>
                                              Subtasks
                                            </div>
                                            {(event as any).subtasks.map((subtask: any, idx: number) => {
                                              const allChecked = subtask.checklistItems && subtask.checklistItems.length > 0 
                                                ? subtask.checklistItems.every((item: any) => item.checked)
                                                : false;
                                              const checkedCount = subtask.checklistItems 
                                                ? subtask.checklistItems.filter((item: any) => item.checked).length 
                                                : 0;
                                              const totalCount = subtask.checklistItems ? subtask.checklistItems.length : 0;
                                              
                                              return (
                                                <div key={idx} className="ml-4 bg-gray-50 rounded p-3">
                                                  <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div className="flex items-center gap-2 flex-1">
                                                      <span className={`material-symbols-outlined text-base ${allChecked ? 'text-green-600' : 'text-gray-400'}`}>
                                                        {allChecked ? 'check_circle' : 'radio_button_unchecked'}
                                                      </span>
                                                      <span className={`font-medium text-sm ${allChecked ? 'line-through text-gray-500' : 'text-text-light'}`}>
                                                        {subtask.title}
                                                      </span>
                                                    </div>
                                                    {totalCount > 0 && (
                                                      <span className={`text-xs px-2 py-1 rounded-full ${allChecked ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                                                        {checkedCount}/{totalCount}
                                                      </span>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Checklist Items */}
                                                  {subtask.checklistItems && subtask.checklistItems.length > 0 && (
                                                    <div className="ml-6 space-y-1.5">
                                                      {subtask.checklistItems.map((item: any, itemIdx: number) => (
                                                        <label key={itemIdx} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 rounded p-1.5 transition-colors">
                                                          <input
                                                            type="checkbox"
                                                            checked={item.checked || false}
                                                            onChange={async (e) => {
                                                              e.stopPropagation();
                                                              try {
                                                                await updateChecklistItem(item.id, e.target.checked);
                                                                await loadScheduleEvents();
                                                              } catch (error) {
                                                                console.error('Failed to update checklist:', error);
                                                              }
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300"
                                                          />
                                                          <span className={`text-sm ${item.checked ? 'line-through text-gray-500' : 'text-text-light'}`}>
                                                            {item.text}
                                                          </span>
                                                        </label>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="p-8 border border-gray-200 rounded-lg text-center bg-white">
                      <span className="material-symbols-outlined text-6xl text-text-light/30 mb-4">view_timeline</span>
                      <p className="text-text-light/60 font-semibold mb-2">
                        No Events Scheduled
                      </p>
                      <p className="text-text-light/40 text-sm">
                        Select a client and job to view scheduled events
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {viewMode === 'Day' && (
              <div id="day-view" className="bg-white min-h-full">
                {/* Job Information Section - Above Timeline */}
                {(() => {
                  const dayEvents = getEventsForDate(currentDate);
                  const jobsForDay = dayEvents.filter(event => {
                    const timeSlot = getEventTimeSlot(event);
                    return timeSlot === null; // Jobs without specific time slots
                  });
                  
                  const tasksCount = dayEvents.filter(event => {
                    const timeSlot = getEventTimeSlot(event);
                    return timeSlot !== null; // Tasks with specific time slots
                  }).length;
                  
                  const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
                  const holiday = isHoliday(currentDate, holidays);
                  
                  return (
                    <>
                      {!isWeekend && !holiday && jobsForDay.length > 0 && (
                        <div className="border-t border-l border-r border-gray-200 bg-primary/5">
                          <div className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="material-symbols-outlined text-primary">work</span>
                              <h4 className="text-sm font-semibold text-text-light">Jobs Scheduled for This Day</h4>
                            </div>
                            <div className="space-y-2">
                              {jobsForDay.map((job) => (
                                <div 
                                  key={job.id}
                                  className="cursor-pointer hover:bg-primary/10 rounded p-2 transition-colors bg-white border border-primary/20"
                                  onDoubleClick={() => setSelectedEvent(job)}
                                  title={`${job.job_number || job.jobNumber} - ${job.job_title || job.jobTitle}\nDouble-click to edit`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`material-symbols-outlined ${getStatusColor(job.status).split(' ')[0]}`}>
                                      {getStatusIcon(job.status)}
                                    </span>
                                    <span className="font-semibold text-primary">
                                      {job.job_number || job.jobNumber}
                                    </span>
                                    <span className="text-text-light flex-1">
                                      {job.job_title || job.jobTitle}
                                    </span>
                                  </div>
                                  {(job.client_name || job.clientName) && (
                                    <p className="text-sm text-text-light/70 ml-8">
                                      {job.client_name || job.clientName}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                            {tasksCount > 0 && (
                              <div className="mt-3 pt-3 border-t border-primary/20">
                                <span className="text-sm text-text-light/70">
                                  {tasksCount} scheduled {tasksCount === 1 ? 'task' : 'tasks'} in timeline below
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {!isWeekend && !holiday && jobsForDay.length === 0 && (
                        <div className="border-t border-l border-r border-gray-200 bg-gray-50/50 p-3 text-center text-sm text-text-light/50">
                          No jobs scheduled for this day
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Day agenda (CR #206) — every dated record that lands on this day */}
                {(() => {
                  const dayItems = agendaByDate.get(ymdLocal(currentDate)) || [];
                  return (
                    <div className="border-t border-l border-r border-gray-200 bg-white">
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-primary">event_note</span>
                          <h4 className="text-sm font-semibold text-text-light">Everything on this day</h4>
                          <span className="text-xs text-text-light/50">({dayItems.length})</span>
                        </div>
                        {dayItems.length === 0 ? (
                          <p className="text-sm text-text-light/50">Nothing dated lands on this day.</p>
                        ) : (
                          <div className="grid gap-1.5 sm:grid-cols-2">
                            {dayItems.map((it, i) => {
                              const meta = AGENDA_META[it.type];
                              return (
                                <button
                                  key={`${it.type}-${i}`}
                                  onClick={() => navigate(it.link)}
                                  className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50/60 px-2.5 py-1.5 text-left hover:border-primary/50 hover:bg-white transition-colors"
                                  title={`${meta.name} — open`}
                                >
                                  <span className={`mt-0.5 inline-flex items-center rounded p-0.5 ring-1 ${meta.chip}`}>
                                    <span className="material-symbols-outlined text-[14px] leading-none">{meta.icon}</span>
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-xs font-semibold text-text-light">{it.label}</span>
                                    {it.sublabel && (
                                      <span className="block truncate text-[11px] text-text-light/60">{it.sublabel}</span>
                                    )}
                                    <span className="block text-[10px] text-text-light/50">
                                      {meta.name}
                                      {it.end_date && it.end_date !== it.date ? ` · ${it.date} → ${it.end_date}` : ''}
                                    </span>
                                  </span>
                                  <span className="material-symbols-outlined mt-0.5 text-sm text-text-light/30">chevron_right</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline Section */}
                <div className="border-t border-l border-gray-200">
                  {Array.from({ length: 8 }, (_, i) => i + 7.5).map((hour) => {
                    const dayEvents = getEventsForDate(currentDate);
                    const dateStr = currentDate.toISOString().split('T')[0];
                    
                    // Get main events (not subtasks)
                    const mainHourEvents = dayEvents.filter(event => {
                      const timeSlot = getEventTimeSlot(event);
                      const currentSlot = Math.floor(hour - 7.5);
                      
                      // Skip events without time slots (jobs are shown in header)
                      if (timeSlot === null) {
                        return false;
                      }
                      
                      return timeSlot === currentSlot;
                    });
                    
                    // Get subtask events for this hour
                    const subtaskEvents = dayEvents.filter(event => {
                      const eventId = (event as any).parent_task_id || (event as any).parentTaskId;
                      if (!eventId) return false;
                      
                      const timeSlot = getEventTimeSlot(event);
                      const currentSlot = Math.floor(hour - 7.5);
                      
                      return timeSlot === currentSlot;
                    });
                    
                    const eventPositions = calculateEventPositions(mainHourEvents);
                    const subtaskPositions = calculateEventPositions(subtaskEvents);
                    
                    return (
                      <div key={hour} className="flex border-b border-gray-200">
                        <div className="w-20 bg-gray-50 p-2 border-r border-gray-200">
                          <p className="text-xs text-text-light/60">
                            {(() => {
                              const displayHour = Math.floor(hour);
                              const minutes = (hour % 1) * 60;
                              return `${displayHour <= 12 ? displayHour : displayHour - 12}:${minutes === 0 ? '00' : '30'} ${displayHour < 12 ? 'AM' : 'PM'}`;
                            })()}
                          </p>
                        </div>
                        <DroppableCell
                          id={(() => {
                            const isWeekend = currentDate.getDay() === 0 || currentDate.getDay() === 6;
                            return isWeekend ? `weekend-${dateStr}-${hour}` : `day-${dateStr}-${hour}`;
                          })()}
                          date={dateStr}
                          hour={hour}
                          className={`flex-1 p-2 h-24 relative transition-all bg-white border-r border-b border-gray-200 ${
                            currentDate.getDay() === 0 || currentDate.getDay() === 6
                              ? 'bg-gray-50 cursor-not-allowed pointer-events-none'
                              : 'hover:border-primary hover:border-2 cursor-pointer'
                          }`}
                          onDoubleClick={currentDate.getDay() !== 0 && currentDate.getDay() !== 6 ? () => handleDateDoubleClick(currentDate) : undefined}
                        >
                          {/* Main Events */}
                          {eventPositions.map(({ event, column, totalColumns }) => {
                            const duration = getEventDuration(event);
                            const heightInRem = duration * 6;
                            const widthPercent = 100 / (totalColumns + subtaskPositions.length);
                            const leftPercent = (column * 100) / (totalColumns + subtaskPositions.length);
                            const hasConflict = hasSchedulingConflict(event);
                            
                            return (
                              <DraggableEvent 
                                key={event.id} 
                                event={event} 
                                onResize={handleEventResize}
                                onDoubleClick={setSelectedEvent}
                                isDragLocked={isDragLocked}
                              >
                                <div
                                  className={`absolute p-2 rounded ${getJobColor(event.job_id || event.jobId).replace('bg-', 'bg-opacity-50 bg-')} ${hasConflict ? 'ring-2 ring-amber-500' : ''} z-50`}
                                  style={{ 
                                    top: '0.5rem',
                                    left: `calc(0.5rem + ${leftPercent}%)`,
                                    width: 'auto',
                                    maxWidth: `calc(${widthPercent}% - 0.5rem)`,
                                    height: `${heightInRem}rem`,
                                    minHeight: '4rem',
                                    minWidth: '150px'
                                  }}
                                >
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className={`material-symbols-outlined text-base ${getStatusColor(event.status).split(' ')[0]}`}>
                                      {getStatusIcon(event.status)}
                                    </span>
                                    {hasConflict && (
                                      <span className="material-symbols-outlined text-base text-amber-500" title="Crew conflict">
                                        warning
                                      </span>
                                    )}
                                    <p className="text-sm font-semibold flex-1">
                                      {event.job_number || event.jobNumber}
                                    </p>
                                  </div>
                                  <p className="text-xs opacity-90">
                                    {event.client_name || event.clientName || ''}
                                  </p>
                                  <p className="text-xs opacity-70 mt-0.5">
                                    {event.start_time || event.startTime || ''} - {event.end_time || event.endTime || ''} ({duration}h)
                                  </p>
                                </div>
                              </DraggableEvent>
                            );
                          })}
                          
                          {/* Subtask Events */}
                          {subtaskPositions.map(({ event, column }) => {
                            const duration = getEventDuration(event);
                            const heightInRem = duration * 6;
                            const totalCols = mainHourEvents.length + subtaskEvents.length;
                            const widthPercent = 100 / totalCols;
                            const leftPercent = ((mainHourEvents.length + column) * 100) / totalCols;
                            
                            return (
                              <DraggableEvent 
                                key={event.id} 
                                event={event} 
                                onResize={handleEventResize}
                                onDoubleClick={setSelectedEvent}
                                isDragLocked={isDragLocked}
                              >
                                <div
                                  className={`absolute p-2 rounded border-2 border-dashed border-current bg-opacity-40 z-40`}
                                  style={{ 
                                    top: '0.5rem',
                                    left: `calc(0.5rem + ${leftPercent}%)`,
                                    width: 'auto',
                                    maxWidth: `calc(${widthPercent}% - 0.5rem)`,
                                    height: `${heightInRem}rem`,
                                    minHeight: '3rem',
                                    minWidth: '120px',
                                    backgroundColor: getJobColor(event.job_id || event.jobId)
                                  }}
                                >
                                  <div className="flex items-center gap-1 mb-1">
                                    <span className="text-xs">▸</span>
                                    <p className="text-xs font-semibold flex-1">
                                      {event.job_title || event.jobTitle}
                                    </p>
                                  </div>
                                  <p className="text-xs opacity-80 mt-0.5">
                                    {event.start_time || event.startTime || ''} - {event.end_time || event.endTime || ''}
                                  </p>
                                </div>
                              </DraggableEvent>
                            );
                          })}
                        </DroppableCell>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'Year' && (
              <div id="year-view" className="bg-white min-h-full">
                <div className="grid grid-cols-3 gap-6 p-6">
                  {Array.from({ length: 12 }, (_, monthIndex) => {
                    const year = currentDate.getFullYear();
                    const firstDay = new Date(year, monthIndex, 1).getDay();
                    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
                    const days = [];
                    
                    for (let i = 0; i < firstDay; i++) {
                      days.push(null);
                    }
                    for (let i = 1; i <= daysInMonth; i++) {
                      days.push(i);
                    }
                    
                    const monthName = new Date(year, monthIndex).toLocaleDateString('en-CA', { month: 'long' });
                    
                    return (
                      <div key={monthIndex} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                        <div 
                          className="bg-gray-50 p-2 text-center border-b border-gray-200 hover:bg-primary/10 cursor-pointer transition-colors"
                          onClick={() => {
                            const newDate = new Date(year, monthIndex, 1);
                            setCurrentDate(newDate);
                            setViewMode('Month');
                          }}
                          title="Click to view this month"
                        >
                          <p className="font-semibold text-sm text-text-light">{monthName} {year}</p>
                        </div>
                        <div className="p-2 bg-white">
                          <div className="grid grid-cols-7 gap-1 mb-1">
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                              <div key={idx} className="text-center text-xs text-text-light/60 font-medium">
                                {day}
                              </div>
                            ))}
                          </div>
                          <div className="grid grid-cols-7 gap-1">
                            {days.map((day, idx) => {
                              const dayEvents = day ? getEventsForMonth(year, monthIndex, day) : [];
                              const cellDate = day ? new Date(year, monthIndex, day) : null;
                              const isWeekend = cellDate ? (cellDate.getDay() === 0 || cellDate.getDay() === 6) : false;
                              const holiday = cellDate ? isHoliday(cellDate, holidays) : null;
                              
                              // Separate jobs from tasks
                              const jobsForDay = dayEvents.filter(event => {
                                const timeSlot = getEventTimeSlot(event);
                                return timeSlot === null;
                              });
                              
                              const tasksForDay = dayEvents.filter(event => {
                                const timeSlot = getEventTimeSlot(event);
                                return timeSlot !== null;
                              });
                              
                              const hasEvents = jobsForDay.length > 0 || tasksForDay.length > 0;
                              
                              return (
                                <div
                                  key={idx}
                                  className={`aspect-square flex items-center justify-center text-xs rounded relative bg-white border border-transparent transition-all ${
                                    day === null
                                      ? ''
                                      : isWeekend
                                      ? 'bg-gray-50 text-text-light/50'
                                      : holiday
                                      ? 'bg-red-100 font-semibold text-red-700 hover:border-red-600 hover:border-2 cursor-pointer'
                                      : hasEvents
                                      ? 'bg-primary/10 font-semibold text-primary hover:border-primary hover:border-2 cursor-pointer'
                                      : 'hover:border-primary hover:border-2 cursor-pointer text-text-light'
                                  }`}
                                  title={
                                    holiday 
                                      ? `${holiday.name} - Click to view day` 
                                      : hasEvents 
                                      ? `${jobsForDay.length} job(s), ${tasksForDay.length} task(s) - Click to view day` 
                                      : day 
                                      ? 'Click to view day' 
                                      : ''
                                  }
                                  onClick={() => {
                                    if (day && !isWeekend) {
                                      const newDate = new Date(year, monthIndex, day);
                                      setCurrentDate(newDate);
                                      setViewMode('Day');
                                    }
                                  }}
                                >
                                  {day}
                                  {holiday && (
                                    <span className="absolute top-0 right-0 text-[8px]" title={holiday.name}>🎉</span>
                                  )}
                                  {hasEvents && !holiday && (
                                    <div className="absolute bottom-0 right-0 flex gap-[1px]">
                                      {jobsForDay.length > 0 && (
                                        <span className="w-1 h-1 bg-primary rounded-full" title={`${jobsForDay.length} job(s)`}></span>
                                      )}
                                      {tasksForDay.length > 0 && (
                                        <span className="w-1 h-1 bg-blue-600 rounded-full" title={`${tasksForDay.length} task(s)`}></span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DragOverlay>
          {activeEvent ? (
            <div className="bg-primary/40 border-l-4 border-primary p-2 rounded-md shadow-lg">
              <p className="text-sm font-bold text-text-light">
                {activeEvent.job_number || activeEvent.jobNumber}
              </p>
              <p className="text-xs text-text-light/70">
                {activeEvent.job_title || activeEvent.jobTitle}
              </p>
            </div>
          ) : null}
        </DragOverlay>
        </DndContext>
      </main>

      {/* Edit Task Modal */}
      {editingEvent && editingJobDetails && (
        <JobSchedulingForm
          isOpen={!!editingEvent}
          onClose={() => {
            setEditingEvent(null);
            setEditingJobDetails(null);
          }}
          onSubmit={async (data: JobScheduleData) => {
            try {
              const jobId = editingJobDetails.id.toString();
              await updateScheduledJob(jobId, data);
              await loadScheduleEvents();
              setEditingEvent(null);
              setEditingJobDetails(null);
            } catch (error) {
              console.error('Failed to update:', error);
              toast.error('Failed to update schedule. Please try again.');
            }
          }}
          isSubmitting={false}
          initialData={{
            id: editingJobDetails.id,
            title: editingJobDetails.title,
            jobNumber: editingJobDetails.jobNumber || editingJobDetails.job_number,
            startDate: editingJobDetails.startDate || editingJobDetails.start_date,
            estimatedEndDate: editingJobDetails.endDate || editingJobDetails.end_date || editingJobDetails.estimatedEndDate || editingJobDetails.estimated_end_date,
            clientId: editingJobDetails.clientId || editingJobDetails.client_id,
            client: editingJobDetails.client || editingJobDetails.clientName || editingJobDetails.client_name,
            siteAddress: editingJobDetails.siteAddress || editingJobDetails.site_address,
            siteCity: editingJobDetails.siteCity || editingJobDetails.site_city,
            siteProvince: editingJobDetails.siteProvince || editingJobDetails.site_province,
            sitePostalCode: editingJobDetails.sitePostalCode || editingJobDetails.site_postal_code,
            parkingInstructions: editingJobDetails.parkingInstructions || editingJobDetails.parking_instructions,
            accessCodes: editingJobDetails.accessCodes || editingJobDetails.access_codes,
            deliveryWindows: editingJobDetails.deliveryWindows || editingJobDetails.delivery_windows,
            clientNotes: editingJobDetails.clientNotes || editingJobDetails.client_notes,
            safetyNotes: editingJobDetails.safetyNotes || editingJobDetails.safety_notes,
          }}
          isEditMode={true}
          editingTaskId={editingEvent.id.toString()}
          extraSection={
            (editingEvent.scheduled_date || editingEvent.scheduledDate) ? (
              <WorkerAssignPanel
                scheduleId={Number(editingEvent.id)}
                scheduledDate={editingEvent.scheduled_date || editingEvent.scheduledDate}
              />
            ) : null
          }
        />
      )}
      {hoursModalDate && (
        <DayLogModal
          date={hoursModalDate}
          dateLabel={hoursDateLabel(hoursModalDate)}
          employees={sortedHoursEmployees}
          myEmployeeId={myEmployeeId}
          jobs={hoursJobs}
          existing={entriesByDate.get(hoursModalDate) || []}
          defaultJobId={jobIdForDate(hoursModalDate)}
          onClose={() => setHoursModalDate(null)}
          onSaved={handleHoursSaved}
          onChanged={loadHoursData}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};


